import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  Navigation, X, MapPin, Loader2, LayoutPanelTop, LayoutPanelLeft, LayoutGrid,
  Crosshair, ChevronRight, Clock, Sparkles,
  Car, Square, Plus, Send, BadgePercent, Radio,
  Sun, Moon, Flame, Share2, Check, Crown, LogOut, Globe, ExternalLink,
  Truck, Plane, Footprints, TrainFront, MoreVertical,
  PartyPopper, Lock, Upload, Trash2, CalendarClock, PlusCircle, Camera, ScrollText, Shuffle, MessageCircle, Coins, Inbox,
} from "lucide-react";
import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { getIcon } from "./iconMap";
import MapSiegePill from "../story/MapSiegePill";
import { GPS } from "@/constants/testIds";
import { useConsumerAuth } from "@/consumer/ConsumerAuth";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { IdentityBadge, BadgeStudio, RankUpSplash } from "@/components/IdentityBadge";
import { RedeemRateShare } from "@/components/RedeemRateShare";
import { GeoFenceAlerts } from "@/components/GeoFenceAlerts";
import { pogConfetti } from "@/lib/pogConfetti";
import { whistleBlast } from "@/lib/pogSounds";
import { sharePogMoment } from "@/lib/pogShareCard";
import { POG_LINGO } from "@/constants/pogLingo";
import { QRCodeSVG } from "qrcode.react";
import { AskPog } from "@/components/AskPog";
import { requestInstall, claimInstallReward } from "@/lib/installPrompt";
import { SponsoredBalloons } from "@/components/SponsoredBalloons";
import { WanderBalloon } from "@/components/WanderBalloon";
import { Fireworks } from "@/components/Fireworks";
import { SkyParade } from "@/components/SkyParade";
import { ShootingStar } from "@/components/ShootingStar";
// PogathonPill import removed — pill retired for map decluttering
import { BlogPeek, CoachMark } from "@/components/MapPeeks";
import { ArrivalCelebration } from "@/components/ArrivalCelebration";
import { DynoStampede } from "@/story/DynoStampede";
import { BalloonFestOverlay } from "@/components/BalloonFestOverlay";
import { CaughtPogBanner } from "@/components/CaughtPogBanner";
import { MechaBoss } from "@/components/MechaBoss";
import { CapacitorPill } from "@/components/CapacitorPill";
import { PogStrip } from "@/components/PogStrip";
import { EventCountdownPill } from "@/components/EventCountdownPill";
import { useGoldenPizzaHunt } from "@/components/useGoldenPizzaHunt";
// ☀️ TODAY IN POG pill removed from the map (user request + audit D2.3) — component lives on in components/DailyDigest.jsx
import { PogDetailPanel } from "@/components/PogDetailPanel";
import { DealsPanel } from "@/components/DealsPanel";
import { PogAdPanel } from "@/components/PogAdPanel";
import { PogExpress } from "@/components/PogExpress";
import Omnibox from "@/components/Omnibox";
import { PogodexSheet } from "@/components/PogodexSheet";
import KingdomSheet, { KingdomTab } from "@/components/KingdomSheet";
import { PogBoardSheet, BOARD_SEEN_KEY } from "@/components/PogBoardSheet";
import { PogScanReticle } from "@/components/PogScanReticle";
import { SpotlightFlyer } from "@/components/SpotlightFlyer";
// PogDayCountdown banner removed — POG DAY hype now auto-posts to THE POG SCROLL
import { ChampSalute } from "@/components/ChampSalute";
import { BusinessStrip } from "@/components/BusinessStrip";
import { useLivePins } from "@/components/useLivePins";
import { InstantRunSheet } from "@/components/InstantRunSheet";
import { QuickFindSheet } from "@/components/QuickFindSheet";
import { FiltersSheet, LocationSearchModal } from "@/components/MapSheets";
import { NavPanel } from "@/components/NavPanel";
import { MapTopBar } from "@/components/MapTopBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { MapMoreMenu } from "@/components/MapMoreMenu";
import { useTranslation } from "react-i18next";
import {
  AIRPOG_LOGO, pinColor, bizPinHtml, AIRPOG_ICON,
  DEFAULT_POS, TILE_URLS, THEME_CYCLE, THEME_FILTERS,
  TRAVEL_MODES, POG_PRESETS, contactAction, bearingDeg, GPS_APPS,
} from "@/lib/mapConstants";

export default function GpsOverlay() {
  const { t } = useTranslation();
  const mapEl = useRef(null);
  const mapRef = useRef(null);

  // 🎯 POG SHOT PULSE — glowing radius rings on the map for every live shot
  useEffect(() => {
    const els = [];
    const t = setTimeout(() => {
      const m = mapRef.current;
      if (!m) return;
      axios.get(`${BACKEND_URL}/api/shots/active`).then((r) => {
        (r.data.shots || []).forEach((s) => {
          if (!s.lat || !s.lng || !mapRef.current) return;
          const c = L.circle([s.lat, s.lng], {
            radius: (s.radius_mi || 3) * 1609, color: "#f97316", weight: 2,
            fillColor: "#f97316", fillOpacity: 0.07, dashArray: "8 8",
          });
          c.addTo(mapRef.current);
          c.bindTooltip(`🎯 POG SHOT — ${s.message}`, { direction: "top" });
          els.push(c);
        });
      }).catch(() => {});
    }, 5000);
    return () => { clearTimeout(t); els.forEach((c) => { try { c.remove(); } catch (e) { /* gone */ } }); };
  }, []);
  const userMarkerRef = useRef(null);
  const bizLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const cruiseLayerRef = useRef(null);
  const stripRef = useRef(null);
  // 🔧 Remount-aware ref: LOGO VIEW toggling unmounts the strip; this tick rebinds the auto-scroll engine.
  const [stripEl, setStripEl] = useState(null);
  const stripRefFn = useCallback((node) => { stripRef.current = node; setStripEl(node); }, []);
  // 🎬 One-time front park (per page load) — NOT re-parked on radar refreshes.
  const stripGraceRef = useRef(Date.now() + 4000);
  const tileRef = useRef(null);
  const clickRef = useRef({ id: null, timer: null });
  const driveRef = useRef({ active: false, path: [], idx: 0, lastSearch: null, timer: null });
  const impressionSet = useRef(new Set());
  const deepLinkActiveRef = useRef(false);
  const watchRef = useRef(null);
  const followRef = useRef(true);
  const followTimerRef = useRef(null);
  const prevPosRef = useRef(null);
  const [heading, setHeading] = useState(null);
  const firstViewRef = useRef(true);
  const navigate = useNavigate();
  const { bizId } = useParams();
  const { pogId } = useParams();
  const [copied, setCopied] = useState(false);
  const { driver, loginGoogle, logout, refresh, toggleFavorite, startPlusCheckout } = useConsumerAuth() || {};
  const [plusOpen, setPlusOpen] = useState(false);
  const [plusNotice, setPlusNotice] = useState(null);
  const [favBusy, setFavBusy] = useState(null);
  const [plusBusy, setPlusBusy] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [inviteDismissed, setInviteDismissed] = useState(() => !!localStorage.getItem("airpog_invite_dismissed"));
  const [blogPeek, setBlogPeek] = useState([]);
  const [wantedCount, setWantedCount] = useState(0);
  const [jobsCount, setJobsCount] = useState(0);
  const [jobsNew, setJobsNew] = useState(0);
  const [hiringSet, setHiringSet] = useState(() => new Set());
  const [reelsCount, setReelsCount] = useState(0);
  const [reelsNow, setReelsNow] = useState(0);
  const [reelPins, setReelPins] = useState([]);
  const [saleNow, setSaleNow] = useState(0);
  const [livePins, setLivePins] = useState([]);
  const [saleAlert, setSaleAlert] = useState(null);
  const [reelStar, setReelStar] = useState(null);

  useEffect(() => {
    axios.get(`${API}/jobs/count`).then((r) => setJobsCount(r.data.count || 0)).catch(() => {});
    axios.get(`${API}/jobs/hiring-ids`).then((r) => setHiringSet(new Set(r.data.ids || []))).catch(() => {});
    if (localStorage.getItem("airpog_was_pogr") === "1")
      axios.get(`${API}/jobs/follows`, { withCredentials: true }).then((r) => setJobsNew(r.data.new_count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    const pull = () => {
      axios.get(`${API}/reels/count`).then((r) => { setReelsCount(r.data.count || 0); setReelsNow(r.data.now || 0); setReelPins(r.data.pins || []); setSaleNow(r.data.sale_now || 0); setReelStar(r.data.star || null); }).catch(() => {});
      axios.get(`${API}/live`).then((r) => setLivePins((r.data.lives || []).filter((l) => l.lat != null))).catch(() => {});
    };
    pull();
    const t = setInterval(pull, 60000);
    return () => clearInterval(t);
  }, []);

  // ---- 🔔 Sale alert — new HAPPENING NOW reel or live broadcast near you ----
  // 🍕 GOLDEN PIZZA HUNT — daily hidden pizza during FEAST events
  useGoldenPizzaHunt(mapRef, driver);
  useLivePins(mapRef, navigate);
  // ---- 🔴 HAPPENING NOW reel + live broadcast pins — pulsing sale markers ----
  const reelMarkersRef = useRef([]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    reelMarkersRef.current.forEach((m) => m.remove());
    const all = [
      ...reelPins.map((p) => ({ ...p, _type: "reel" })),
      ...livePins.map((l) => ({ ...l, _type: "live" })),
    ];
    reelMarkersRef.current = all.map((p) => {
      const beamed = p._type === "live" && p.beam_place;
      const emoji = p._type === "live" ? (beamed ? "⚛️" : "📡") : "🎬";
      const ring = beamed ? "rgba(56,189,248,0.6)" : "rgba(239,68,68,0.55)";
      const core = beamed ? "background:#0284c7;box-shadow:0 0 12px 3px rgba(56,189,248,0.85)" : "background:#dc2626;box-shadow:0 2px 8px rgba(220,38,38,0.6)";
      const icon = L.divIcon({
        className: p._type === "live" ? "live-pin-wrap" : "reel-pin-wrap",
        html: `<div style="position:relative;width:30px;height:30px" title="${(p.title || "").replace(/"/g, "")}">
                 <span class="animate-ping" style="position:absolute;inset:0;border-radius:9999px;background:${ring}"></span>
                 <span style="position:absolute;inset:3px;border-radius:9999px;${core};display:flex;align-items:center;justify-content:center;font-size:13px">${emoji}</span>
               </div>`,
        iconSize: [30, 30], iconAnchor: [15, 15],
      });
      const m = L.marker([p.lat, p.lng], { icon, zIndexOffset: 900 }).addTo(map);
      m.on("click", () => navigate(p._type === "live" ? `/live/${p.id}` : "/reels"));
      return m;
    });
    return () => { reelMarkersRef.current.forEach((m) => m.remove()); reelMarkersRef.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelPins, livePins]);

  // 📸 PIC PINS — deal-photo thumbnails right on the map
  const [picPins, setPicPins] = useState([]);
  const picMarkersRef = useRef([]);
  useEffect(() => {
    axios.get(`${API}/flics/map-pins`).then((r) => setPicPins(r.data.pins || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    picMarkersRef.current.forEach((m) => m.remove());
    picMarkersRef.current = picPins.map((p) => {
      const ring = p.champ ? "#fde047" : "#fbbf24";
      const glow = p.champ ? "0 0 14px 3px rgba(253,224,71,0.75)" : "0 2px 10px rgba(0,0,0,0.55)";
      const icon = L.divIcon({
        className: "pic-pin-wrap",
        html: `<div data-testid="pic-pin-${p.id}" style="position:relative;width:44px;height:44px;border-radius:9999px;border:2.5px solid ${ring};box-shadow:${glow}" title="${p.champ ? "👑 HUNTER CHAMP · " : ""}${(p.caption || "").replace(/"/g, "")}">
                 <img src="${API}/flics/${p.id}/image" style="width:100%;height:100%;object-fit:cover;border-radius:9999px;background:#0f172a" loading="lazy"/>
                 <span style="position:absolute;top:-9px;right:-6px;font-size:${p.champ ? 15 : 13}px;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.6))">${p.champ ? "👑" : "📸"}</span>
               </div>`,
        iconSize: [44, 44], iconAnchor: [22, 22],
      });
      const m = L.marker([p.lat, p.lng], { icon, zIndexOffset: 850 }).addTo(map);
      m.on("click", () => navigate(`/flics?biz=${p.business_id || "pin"}&bizname=${encodeURIComponent("📍 Pics at this spot")}&blat=${p.lat}&blng=${p.lng}`));
      return m;
    });
    return () => { picMarkersRef.current.forEach((m) => m.remove()); picMarkersRef.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picPins]);

  // 🔥 Streak-at-risk reminder — evening POG BOT beep before midnight snaps it
  const [streakRisk, setStreakRisk] = useState(0);
  useEffect(() => {
    if (new Date().getHours() < 17) return;
    if (localStorage.getItem("airpog_was_pogr") !== "1") return; // 🧹 anon visitors skip the session poll
    axios.get(`${API}/flics/streak`, { withCredentials: true }).then((r) => {
      if (!r.data.posted_today && (r.data.streak || 0) >= 2) setStreakRisk(r.data.streak);
    }).catch(() => {});
  }, []);

  // 🔥 Deal heat glow — soft amber halos where live deals cluster
  const [heatPts, setHeatPts] = useState([]);
  const heatLayerRef = useRef([]);
  useEffect(() => {
    axios.get(`${API}/deal-scout/heat`).then((r) => setHeatPts(r.data.points || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    heatLayerRef.current.forEach((c) => c.remove());
    // sparse heat DOTS — spaced wider than their radius so they can never fuse into a blob
    const seen = new Set();
    const pts = heatPts.filter((p) => {
      const k = `${Math.round(p.lat / 0.006)}:${Math.round(p.lng / 0.006)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).slice(0, 60);
    heatLayerRef.current = pts.map((p) =>
      L.circle([p.lat, p.lng], { radius: 130, stroke: false, fillColor: "#f59e0b", fillOpacity: 0.16, interactive: false }).addTo(m));
    const syncHeat = () => { // heat reads at street zooms only — hide at city scale
      const op = m.getZoom() >= 13 ? 0.16 : 0;
      heatLayerRef.current.forEach((c) => c.setStyle({ fillOpacity: op }));
    };
    syncHeat();
    m.on("zoomend", syncHeat);
    return () => { m.off("zoomend", syncHeat); heatLayerRef.current.forEach((c) => c.remove()); heatLayerRef.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatPts]);

  // 🕵️🔔 New-deal-nearby beeps — scout drops within a mile ping every 5 min
  const [userPos, setUserPos] = useState(null);
  const scoutPosRef = useRef(null);
  scoutPosRef.current = userPos;
  useEffect(() => {
    if (!userPos) return;
    const check = () => {
      const p = scoutPosRef.current;
      if (!p) return;
      const since = localStorage.getItem("airpog_scoutnew_ts") || "";
      axios.get(`${API}/deal-scout/nearby-new`, { params: { lat: p.lat, lng: p.lng, since } }).then((r) => {
        localStorage.setItem("airpog_scoutnew_ts", r.data.now);
        if (r.data.deal) setSaleAlert({ type: "scout", raw: `🕵️🔔 NEW REAL DEAL just dropped ${r.data.deal.dist} mi away — "${r.data.deal.title}" at ${r.data.deal.name}! It's on the map NOW!`, dist: null });
      }).catch(() => {});
    };
    check();
    const t = setInterval(check, 300000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!userPos]);

  // 📰 Daily deal digest — fresh REAL scouted deals near you, once a day
  const [digest, setDigest] = useState(null);
  useEffect(() => {
    if (!userPos) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem("airpog_digest_day") === today) return;
    axios.get(`${API}/deal-scout/digest`, { params: { lat: userPos.lat, lng: userPos.lng } }).then((r) => {
      if (r.data.count > 0) { setDigest(r.data); localStorage.setItem("airpog_digest_day", today); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPos]);

  const [blogPeekDismissed, setBlogPeekDismissed] = useState(() => !!sessionStorage.getItem("airpog_blogpeek_dismissed"));
  useEffect(() => {
    if (blogPeek.length > 0 && !blogPeekDismissed) {
      const t = setTimeout(() => { setBlogPeekDismissed(true); sessionStorage.setItem("airpog_blogpeek_dismissed", "1"); }, 5000);
      return () => clearTimeout(t);
    }
  }, [blogPeek, blogPeekDismissed]);
  const alertedRef = useRef(new Set());
  const [dealAlert, setDealAlert] = useState(null);
  // 🎬 POG REELS intro pill removed (map cleanup)

  const [calm, setCalm] = useState(() => {
    // 🎪 CLEAN MAP BY DEFAULT for everyone — the sky show (trains, balloons, fleet)
    // only runs when someone taps the tent button. Their choice is remembered.
    return localStorage.getItem("airpog_calm") !== "0";
  });
  const toggleCalm = () => setCalm((c) => { const n = !c; localStorage.setItem("airpog_calm", n ? "1" : "0"); return n; });
  const [whistleOn, setWhistleOn] = useState(() => localStorage.getItem("airpog_whistle") !== "0");
  const toggleWhistle = () => setWhistleOn((w) => { const n = !w; localStorage.setItem("airpog_whistle", n ? "1" : "0"); return n; });
  const [searchPos, setSearchPos] = useState(null);
  // 👑 TOP RUNNER of the week — gold crown roams the map (+ one-time champ fete)
  const [runChamp, setRunChamp] = useState(null);
  useEffect(() => {
    axios.get(`${API}/runs/leaderboard`).then((r) => {
      const c = r.data.champ;
      if (!c) return;
      setRunChamp(c);
      if (driver && c.user_id === driver.user_id && !localStorage.getItem(`airpog_champ_fete_${c.week}`)) {
        localStorage.setItem(`airpog_champ_fete_${c.week}`, "1");
        confetti({ particleCount: 160, spread: 95, origin: { y: 0.35 } });
        toast.success("👑 YOU are TOP RUNNER of the week! +100 POG BUCKS — wear that crown!", { duration: 6000 });
      }
    }).catch(() => {});
  }, [driver]);
  // 🧳 TRAVELER MODE — POG'N 100+ mi from home base? Point them at the Local Travel Scout.
  const [travelerMode, setTravelerMode] = useState(false);
  useEffect(() => {
    if (!userPos) return;
    try {
      const raw = localStorage.getItem("airpog_home_base");
      if (!raw) { localStorage.setItem("airpog_home_base", JSON.stringify({ lat: userPos.lat, lng: userPos.lng })); return; }
      const far = distMiles(JSON.parse(raw), userPos) > 100;
      if (far && localStorage.getItem("airpog_traveler_seen") !== new Date().toDateString()) setTravelerMode(true);
      else if (!far) setTravelerMode(false);
    } catch { /* ignore */ }
  }, [userPos]);
  const dismissTraveler = () => { setTravelerMode(false); localStorage.setItem("airpog_traveler_seen", new Date().toDateString()); };
  // 🚢 POG LINER honk — deep two-tone ship horn (respects the whistle toggle)
  const linerHonk = () => {
    if (!whistleOn) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [110, 138].forEach((hz) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sawtooth"; o.frequency.value = hz;
        g.gain.setValueAtTime(0.12, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.3);
        o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 1.3);
      });
    } catch { /* no audio */ }
  };
  // 🗓 TRIP COUNTDOWN chip + 🎉 trip-day bon voyage
  const [myTrip, setMyTrip] = useState(null);
  useEffect(() => {
    if (!driver) { setMyTrip(null); return; }
    axios.get(`${API}/travel/trip`, { withCredentials: true }).then((r) => {
      const t = r.data.trip;
      setMyTrip(t);
      if (t && t.days_left === 0 && !localStorage.getItem(`airpog_tripday_${t.date}`)) {
        localStorage.setItem(`airpog_tripday_${t.date}`, "1");
        confetti({ particleCount: 140, spread: 90, origin: { y: 0.4 } });
        toast.success(`🎉 BON VOYAGE — it's trip day to ${t.dest}! POG SAFE out there! 🧳`, { duration: 7000 });
      }
    }).catch(() => {});
  }, [driver]);

  // ---- 🔔 Sale alert — new HAPPENING NOW reel or live broadcast near you ----
  useEffect(() => {
    if (!userPos) return;
    const seen = new Set(JSON.parse(localStorage.getItem("airpog_sale_alerts") || "[]"));
    const cand = [
      ...livePins.map((l) => ({ type: "live", id: l.id, title: l.title, name: l.name, lat: l.lat, lng: l.lng, beamed: !!l.beam_place })),
      ...reelPins.map((p) => ({ type: "reel", id: p.id, title: p.title, lat: p.lat, lng: p.lng })),
    ].find((c) => !seen.has(`${c.type}:${c.id}`) && distMiles(userPos, c) <= 30);
    if (cand) {
      cand.dist = distMiles(userPos, cand).toFixed(1);
      seen.add(`${cand.type}:${cand.id}`);
      localStorage.setItem("airpog_sale_alerts", JSON.stringify([...seen].slice(-100)));
      setSaleAlert(cand);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelPins, livePins, userPos]);

  useEffect(() => {
    if (!saleAlert) return;
    const t = setTimeout(() => setSaleAlert(null), 12000);
    return () => clearTimeout(t);
  }, [saleAlert]);

  // ---- 🔔 Follower alerts — sellers you follow just posted / went live ----
  useEffect(() => {
    if (!driver) return;
    const pull = () => axios.get(`${API}/reels/follow-feed`, { withCredentials: true }).then((r) => {
      const a = (r.data.alerts || [])[0];
      const n = (r.data.new || [])[0];
      if (a) {
        if ((a.text || "").includes("POG WHISTLE")) whistleBlast();
        setSaleAlert(a.type === "blog" ? { type: "blog", slug: a.slug || null, raw: a.text, dist: null } : a.type === "board" ? { type: "board", raw: a.text, dist: null } : a.type === "hunter_champ" ? { type: "champ", raw: a.text, dist: null } : a.type === "streak_golden" ? { type: "golden", raw: a.text, dist: null } : a.type === "podium" ? { type: "podium", raw: a.text, dist: null } : a.type === "friend_req" || a.type === "friend" || a.type === "crewchat" ? { type: "crew", raw: a.text, dist: null } : a.type === "gram" ? { type: "gram", id: a.gram_code || null, raw: a.text, dist: null } : a.type === "repog" || a.type === "bomb" ? { type: "pic", raw: a.text, dist: null } : { type: a.type === "live_reminder" ? "live" : "reel", id: a.live_id || null, raw: a.text, dist: null });
      } else if (n) {
        setSaleAlert({ type: n.type, id: n.id, title: n.title, name: n.name, follow: true, dist: null });
        axios.post(`${API}/reels/follow-seen`, {}, { withCredentials: true }).catch(() => {});
      }
    }).catch(() => {});
    pull();
    const t = setInterval(pull, 90000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null); // null = random/all
  const [radius, setRadius] = useState(10);
  const [truckerMode, setTruckerMode] = useState(() => localStorage.getItem("airpog_trucker_mode") === "1");
  const [travelMode, setTravelMode] = useState(() => {
    const m = localStorage.getItem("airpog_travel_mode");
    return TRAVEL_MODES[m] ? m : "drive";
  });
  const [exploreActive, setExploreActive] = useState(false);
  const [exploreLabel, setExploreLabel] = useState("");
  // 🎯 POG SCAN — POINT/HOLD/RELEASE gesture state
  const [scanFx, setScanFx] = useState(null);
  const holdRef = useRef({ timer: null, armed: false, pt: null, suppressClick: false });
  const [scanHistory, setScanHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("airpog_scan_history") || "[]"); } catch { return []; }
  });
  const [layout, setLayout] = useState("horizontal");
  const [showCoach, setShowCoach] = useState(false);
  const coachShownRef = useRef(false);
  const [dealPulse, setDealPulse] = useState(false);
  const prevDealCountRef = useRef(0);
  const [mapTheme, setMapTheme] = useState("day");
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warming, setWarming] = useState(false);
  const [poggedZone, setPoggedZone] = useState(false);
  const [spotlightIds, setSpotlightIds] = useState(() => new Map()); // 🎈 business_id → spotlight near searchPos
  const [zoneLoading, setZoneLoading] = useState(null); // 🚚 territory importing — label or null
  const [zoneWelcome, setZoneWelcome] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [nav, setNav] = useState(null);
  const [routing, setRouting] = useState(false);
  const [driving, setDriving] = useState(false);
  const [ads, setAds] = useState(null);
  const [adsLoading, setAdsLoading] = useState(false);
  const [redeem, setRedeem] = useState(null); // active redemption {token, code, ...}
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [checkinBusy, setCheckinBusy] = useState(false);
  const [checkinMsg, setCheckinMsg] = useState(null);
  const [visitQr, setVisitQr] = useState(null); // {token, scan_url, qr, reward, business_name}
  const [visitQrBusy, setVisitQrBusy] = useState(false);
  const [arrivalCelebrated, setArrivalCelebrated] = useState(null); // business_id already celebrated
  const [showArrival, setShowArrival] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [sharingMoment, setSharingMoment] = useState(false);
  const [dmCompose, setDmCompose] = useState(null); // {sale_id, title, contact}
  const [dmText, setDmText] = useState("");
  const [dmBusy, setDmBusy] = useState(false);
  const [dmSent, setDmSent] = useState(false);
  const [dmUnread, setDmUnread] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const [dealOpen, setDealOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [dealForm, setDealForm] = useState({ title: "", detail: "", discount: "", expires: "Limited time" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [dailyUnclaimed, setDailyUnclaimed] = useState(false);
  const [pogzilla, setPogzilla] = useState(false);
  const [caughtPog, setCaughtPog] = useState(null);
  const [pogOpen, setPogOpen] = useState(false);
  const [pogForm, setPogForm] = useState(null);
  const [lingoIdx, setLingoIdx] = useState(0);
  const [pogPhotoFile, setPogPhotoFile] = useState(null);
  const [pogBusy, setPogBusy] = useState(false);
  const [pogShare, setPogShare] = useState(null);
  const [pogDetail, setPogDetail] = useState(null);
  const [pogAd, setPogAd] = useState(null);
  const [myPogs, setMyPogs] = useState([]);
  const [geoUncertain, setGeoUncertain] = useState(false);
  const [geoDismissed, setGeoDismissed] = useState(false);
  const [detectedCity, setDetectedCity] = useState("");
  const [dealsOnly, setDealsOnly] = useState(false);
  const [milestoneToast, setMilestoneToast] = useState(null); // {title, views, milestone}
  const [placingBiz, setPlacingBiz] = useState(false);
  const [addBizOpen, setAddBizOpen] = useState(false);
  const [addBizForm, setAddBizForm] = useState(null);
  const [addBizBusy, setAddBizBusy] = useState(false);
  const [addBizDone, setAddBizDone] = useState(null);
  const [voted, setVoted] = useState({});

  const appInstalled = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const promptInstall = async () => {
    const outcome = await requestInstall();
    if (outcome === "accepted") { setError(null); return; }
    if (outcome === "dismissed") return;
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const inApp = /tiktok|fban|fbav|instagram|snapchat|twitter|micromessenger/i.test(navigator.userAgent);
    setError(inApp
      ? "This in-app browser can't install apps. Open airpog.com in Chrome, then tap Install."
      : ios
      ? "On iPhone: tap the Share icon, then “Add to Home Screen” to install AIR.POG."
      : "To install: open Chrome's menu (⋮) and choose “Add to Home screen” → “Install”.");
  };
  const [error, setError] = useState(null);
  const [locOpen, setLocOpen] = useState(false);
  const [locQuery, setLocQuery] = useState("");
  const [locResults, setLocResults] = useState([]);
  const [locSearching, setLocSearching] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [askQuery, setAskQuery] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [askResult, setAskResult] = useState(null);
  const [gpsSheet, setGpsSheet] = useState(null);
  const [pogodexOpen, setPogodexOpen] = useState(false);
  const [kingdomOpen, setKingdomOpen] = useState(false);
  useEffect(() => {
    const h = () => setKingdomOpen(true);
    window.addEventListener("airpog:open-board", h);
    return () => window.removeEventListener("airpog:open-board", h);
  }, []);
  const [omniOpen, setOmniOpen] = useState(false);
  const [badgeStudioOpen, setBadgeStudioOpen] = useState(false);
  // ⚜ "show on map" — fly to a spot handed over via localStorage (LTS, POG VISION…).
  // If a biz_id rides along, wait for the business list and open its deal panel too.
  const flyHandoffRef = useRef({});
  useEffect(() => { flyHandoffRef.current = { businesses, handleTabClick }; });
  useEffect(() => {
    let tries = 0;
    const t = setInterval(() => {
      if (!mapRef.current) return;
      const raw = localStorage.getItem("airpog_fly_to");
      if (!raw) { clearInterval(t); return; }
      try {
        const tgt = JSON.parse(raw);
        if (!(tgt.lat && tgt.lng)) { localStorage.removeItem("airpog_fly_to"); clearInterval(t); return; }
        const { businesses: list } = flyHandoffRef.current;
        const b = tgt.biz_id ? (list || []).find((x) => x.id === tgt.biz_id) : null;
        tries += 1;
        if (tgt.biz_id && !b && tries < 8) return; // give the business list a moment to load
        localStorage.removeItem("airpog_fly_to");
        clearInterval(t);
        mapRef.current.flyTo([tgt.lat, tgt.lng], 17, { duration: 1.6 });
        if (tgt.biz_id) {
          // open the deal panel once the merchant is in the list (may load after the fly-in)
          let waits = 0;
          let fetched = false;
          const t2 = setInterval(() => {
            const { businesses: l2, handleTabClick: openIt } = flyHandoffRef.current;
            const b2 = (l2 || []).find((x) => x.id === tgt.biz_id);
            waits += 1;
            if (b2 && waits >= 4) { clearInterval(t2); openIt?.(b2); }
            else if (waits === 8 && !b2 && !fetched) {
              // map list doesn't cover this area — pull the merchant straight from the feed
              fetched = true;
              axios.get(`${API}/businesses?lat=${tgt.lat}&lng=${tgt.lng}&radius=1`).then((r) => {
                const hit = (r.data || []).find((x) => x.id === tgt.biz_id);
                if (hit) { clearInterval(t2); flyHandoffRef.current.handleTabClick?.(hit); }
              }).catch(() => {});
            }
            else if (waits > 24) clearInterval(t2);
          }, 500);
        }
      } catch (e) { localStorage.removeItem("airpog_fly_to"); clearInterval(t); }
    }, 500);
    return () => clearInterval(t);
  }, []);
  const [rankUp, setRankUp] = useState(null);
  useEffect(() => {
    if (!driver || sessionStorage.getItem("airpog_rank_ack")) return;
    sessionStorage.setItem("airpog_rank_ack", "1");
    axios.post(`${API}/badge-rank/ack`, {}, { withCredentials: true }).then((r) => {
      if (r.data.rank_up) { setRankUp(r.data); pogConfetti(); }
      const perk = r.data.perk_bonus || 0;
      const tierName = r.data.rank?.tier?.name;
      if (perk > 0 && tierName) setTimeout(() => toast.success(`⚡ ${tierName} PERK paid you +${perk} bonus pts since your last visit!`, { duration: 4000 }), 600);
    }).catch(() => {});
    axios.post(`${API}/pog-badges/ack`, {}, { withCredentials: true }).then((r) => {
      const fresh = r.data.new || [];
      fresh.slice(0, 3).forEach((b, i) => {
        setTimeout(() => toast.success(`🎖 BADGE UNLOCKED — ${b.emoji} ${b.name}!`, { duration: 3500 }), 1200 + i * 1100);
      });
      if (fresh.length > 3) setTimeout(() => toast(`🎖 +${fresh.length - 3} more badges in your case — check your profile!`), 1200 + 3 * 1100);
      if (fresh.length) setTimeout(() => pogConfetti(), 1400);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver]);
  const [badgeVer, setBadgeVer] = useState(() => { try { return localStorage.getItem("airpog_badge_ver") || ""; } catch (e) { return ""; } });
  useEffect(() => {
    const onBadge = () => { try { setBadgeVer(localStorage.getItem("airpog_badge_ver") || String(Date.now())); } catch (e) { setBadgeVer(String(Date.now())); } };
    window.addEventListener("airpog:badge-changed", onBadge);
    return () => window.removeEventListener("airpog:badge-changed", onBadge);
  }, []);

  // 🌡️ HEATMAP CLUSTERING — at city zoom the pins dissolve into glowing deal zones;
  // zoom into a glowing neighborhood and the individual store pins fade back in.
  const clustersRef = useRef([]);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const clear = () => { clustersRef.current.forEach((c) => c.remove()); clustersRef.current = []; };
    const apply = () => {
      const pane = m.getPane("markerPane");
      clear();
      if (m.getZoom() >= 13 || businesses.length === 0) {
        if (pane) { pane.style.opacity = ""; pane.style.pointerEvents = ""; }
        return;
      }
      if (pane) { pane.style.opacity = "0"; pane.style.pointerEvents = "none"; }
      const grid = {};
      businesses.forEach((b) => {
        const k = `${Math.round(b.lat / 0.014)}:${Math.round(b.lng / 0.014)}`;
        const g = (grid[k] = grid[k] || { lat: 0, lng: 0, n: 0, deals: 0 });
        g.lat += b.lat; g.lng += b.lng; g.n += 1; g.deals += b.has_deals ? 1 : 0;
      });
      Object.values(grid).forEach((g) => {
        const lat = g.lat / g.n, lng = g.lng / g.n;
        const hot = g.deals > 0;
        const circle = L.circle([lat, lng], {
          radius: Math.min(400 + g.n * 60, 1600), stroke: false,
          fillColor: hot ? "#f59e0b" : "#8b5cf6", fillOpacity: hot ? 0.3 : 0.2,
        }).addTo(m);
        circle.on("click", () => m.setView([lat, lng], 14));
        const label = L.marker([lat, lng], {
          pane: "tooltipPane", interactive: false,
          icon: L.divIcon({ className: "", iconSize: [0, 0], html: `<div style="transform:translate(-50%,-50%);white-space:nowrap;font-weight:900;font-size:11px;color:#fff;background:${hot ? "rgba(217,119,6,.92)" : "rgba(124,58,237,.92)"};padding:2px 8px;border-radius:999px;box-shadow:0 2px 8px rgba(0,0,0,.4)">${hot ? `🔥 ${g.deals} deal${g.deals === 1 ? "" : "s"}` : `${g.n} spots`}</div>` }),
        }).addTo(m);
        clustersRef.current.push(circle, label);
      });
    };
    apply();
    m.on("zoomend", apply);
    return () => {
      m.off("zoomend", apply);
      clear();
      const pane = m.getPane("markerPane");
      if (pane) { pane.style.opacity = ""; pane.style.pointerEvents = ""; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses]);
  // 📜 THE POG SCROLL — HQ notifications board (unread dot when a newer post exists)
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardUnread, setBoardUnread] = useState(false);
  useEffect(() => {
    axios.get(`${API}/board?limit=1`).then((r) => {
      const latest = r.data.latest_ts;
      if (latest && latest > (localStorage.getItem(BOARD_SEEN_KEY) || "")) setBoardUnread(true);
    }).catch(() => {});
    if (new URLSearchParams(window.location.search).get("scroll") === "1") setBoardOpen(true);
  }, []);
  const [markAlert, setMarkAlert] = useState(null);
  const markAlertT = useRef(null);
  const [markTick, setMarkTick] = useState(0);
  useEffect(() => {
    const bump = () => setMarkTick((t) => t + 1);
    window.addEventListener("airpog:marks-changed", bump);
    return () => window.removeEventListener("airpog:marks-changed", bump);
  }, []);

  // 🔥 HOT THIS WEEK — trending merchants (feeds pin flair + POG'ODEX section), refreshed at most every 60s
  const [hot, setHot] = useState([]);
  const hotFetchedAt = useRef(0);
  useEffect(() => {
    if (!businesses.length || Date.now() - hotFetchedAt.current < 60000) return;
    hotFetchedAt.current = Date.now();
    axios.post(`${API}/pogodex/trending`, { ids: businesses.map((b) => b.id).slice(0, 300) })
      .then((r) => setHot(r.data.hot || [])).catch(() => {});
  }, [businesses]);

  // 📖 One-time coach bubble pointing first-time POG'Rs at the POG'ODEX button
  const [odexTip, setOdexTip] = useState(false);
  useEffect(() => {
    if (!businesses.length || pogodexOpen) return;
    if (!localStorage.getItem("airpog_onboarded") || localStorage.getItem("airpog_odex_tip_seen")) return;
    const show = setTimeout(() => setOdexTip(true), 12000);
    return () => clearTimeout(show);
  }, [businesses.length, pogodexOpen]);
  useEffect(() => {
    if (!odexTip) return;
    const hide = setTimeout(() => dismissOdexTip(), 9000);
    return () => clearTimeout(hide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odexTip]);
  const dismissOdexTip = () => { setOdexTip(false); localStorage.setItem("airpog_odex_tip_seen", "1"); };

  // 🏛️ POG WALK fame ticker removed (map cleanup — moved to POG SCROLL era)
  const ASK_SUGGESTIONS = ["Lunch deals nearby", "Cheapest gas", "Open pharmacy", "Coffee with a deal", "Quick bite under 5 min"];

  // ---- init map once ----
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(mapEl.current, { zoomControl: false, attributionControl: true }).setView(
      [DEFAULT_POS.lat, DEFAULT_POS.lng], 15
    );
    tileRef.current = L.tileLayer(TILE_URLS.day, {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; CARTO',
      maxZoom: 20,
    }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    bizLayerRef.current = L.layerGroup().addTo(map);
    cruiseLayerRef.current = L.layerGroup().addTo(map);
    routeLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // Pause follow when the driver manually pans, but auto-resume after a few seconds (so a bump doesn't freeze tracking).
    map.on("dragstart", () => {
      if (driveRef.current.active) return;
      followRef.current = false;
      clearTimeout(followTimerRef.current);
      followTimerRef.current = setTimeout(() => { followRef.current = true; }, 8000);
    });
    // Tap anywhere on the map to scan deals around that spot (explore mode).
    map.on("click", (e) => {
      if (driveRef.current.active) return;
      if (holdRef.current.suppressClick) return; // POG SCAN release already handled it
      setExploreRef.current && setExploreRef.current(e.latlng.lat, e.latlng.lng, "this spot");
    });
    // Disable the dot's glide transition during zoom (Leaflet rewrites transforms then) so it doesn't drift.
    map.on("zoomstart", () => {
      const el = userMarkerRef.current?.getElement?.();
      if (el) el.style.transition = "none";
    });
    map.on("zoomend", () => {
      const el = userMarkerRef.current?.getElement?.();
      if (el) requestAnimationFrame(() => { el.style.transition = "transform 0.65s linear"; });
    });
  }, []);

  // 🎯 POG SCAN — POINT anywhere, HOLD to arm the reticle, RELEASE to radar-scan that area.
  useEffect(() => {
    const el = mapRef.current?.getContainer?.();
    if (!el) return;
    const h = holdRef.current;
    const cancel = () => {
      clearTimeout(h.timer);
      if (h.armed) setScanFx(null);
      h.armed = false;
      h.pt = null;
    };
    const down = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (driveRef.current.active || h.pt) { cancel(); return; } // second finger = pinch, bail
      h.pt = { x: e.clientX, y: e.clientY };
      clearTimeout(h.timer);
      h.timer = setTimeout(() => {
        h.armed = true;
        try { navigator.vibrate && navigator.vibrate(25); } catch (err) { /* unsupported */ }
        setScanFx({ x: h.pt.x, y: h.pt.y, phase: "arming" });
      }, 500);
    };
    const move = (e) => {
      if (h.pt && Math.hypot(e.clientX - h.pt.x, e.clientY - h.pt.y) > 14) cancel();
    };
    const up = (e) => {
      clearTimeout(h.timer);
      const wasArmed = h.armed;
      h.armed = false;
      const pt = h.pt;
      h.pt = null;
      if (!wasArmed || !pt || !mapRef.current) return;
      h.suppressClick = true;
      setTimeout(() => { h.suppressClick = false; }, 400);
      const rect = el.getBoundingClientRect();
      const ll = mapRef.current.containerPointToLatLng([pt.x - rect.left, pt.y - rect.top]);
      setScanFx({ x: pt.x, y: pt.y, phase: "sweep" });
      try { navigator.vibrate && navigator.vibrate([15, 40, 15]); } catch (err) { /* unsupported */ }
      Promise.all([
        axios.get(`${API}/reverse-geocode`, { params: { lat: ll.lat, lng: ll.lng } }).then((r) => r.data?.city || "").catch(() => ""),
        new Promise((res) => setTimeout(res, 1000)),
      ]).then(([city]) => {
        setScanFx(null);
        const label = city || "this spot";
        setExploreRef.current && setExploreRef.current(ll.lat, ll.lng, label);
        if (city) {
          try {
            const hist = JSON.parse(localStorage.getItem("airpog_scan_history") || "[]").filter((s) => s.label !== city);
            hist.unshift({ lat: ll.lat, lng: ll.lng, label: city, ts: Date.now() });
            localStorage.setItem("airpog_scan_history", JSON.stringify(hist.slice(0, 4)));
            setScanHistory(hist.slice(0, 4));
          } catch (err) { /* storage full */ }
        }
      });
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
    };
  }, []);

  // 👥 POG'ALONG — fly to a squad member handed off from /pogalong, and quietly
  // share my location with the squad while the map is open (only if sharing is ON).
  useEffect(() => {
    let marker = null;
    try {
      const raw = localStorage.getItem("airpog_flyto");
      if (raw) {
        localStorage.removeItem("airpog_flyto");
        const f = JSON.parse(raw);
        if (f && Date.now() - (f.ts || 0) < 15000 && mapRef.current) {
          mapRef.current.setView([f.lat, f.lng], 15);
          marker = L.circleMarker([f.lat, f.lng], { radius: 11, color: "#38bdf8", weight: 3, fillColor: "#0ea5e9", fillOpacity: 0.5 })
            .addTo(mapRef.current)
            .bindTooltip(`👥 ${f.label || "POG'ALONG"}`, { permanent: true, direction: "top", offset: [0, -12] });
        }
      }
    } catch (e) { /* bad handoff */ }
    let timer = null;
    let squadLayer = null;
    const drawSquad = (members) => {
      if (!mapRef.current) return;
      if (squadLayer) { mapRef.current.removeLayer(squadLayer); squadLayer = null; }
      const withLoc = (members || []).filter((m) => m.loc);
      if (!withLoc.length) return;
      squadLayer = L.layerGroup();
      withLoc.forEach((m) => {
        if (m.trail && m.trail.length > 1) {
          const pts = m.trail.map((p) => [p.lat, p.lng]);
          // ✨ TRAIL GLOW — soft neon underlay beneath the breadcrumb dash
          L.polyline(pts, { color: "#22d3ee", weight: 10, opacity: 0.16 }).addTo(squadLayer);
          L.polyline(pts, { color: "#67e8f9", weight: 5, opacity: 0.22 }).addTo(squadLayer);
          L.polyline(pts, { color: "#38bdf8", weight: 2.5, opacity: 0.75, dashArray: "4 6" }).addTo(squadLayer);
        }
        L.marker([m.loc.lat, m.loc.lng], {
          icon: L.divIcon({
            className: "",
            html: `<div style="width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#0ea5e9,#8b5cf6);border:2.5px solid #e0f2fe;box-shadow:0 0 14px rgba(56,189,248,0.8);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;color:#fff">${(m.name || "P").slice(0, 1).toUpperCase()}</div>`,
            iconSize: [34, 34], iconAnchor: [17, 17],
          }),
          zIndexOffset: 900,
        }).bindTooltip(`👥 ${m.name}`, { direction: "top", offset: [0, -18] }).addTo(squadLayer);
      });
      squadLayer.addTo(mapRef.current);
    };
    if (driver) {
      const push = () => navigator.geolocation?.getCurrentPosition(
        (p) => axios.post(`${API}/pogalong/location`, { lat: p.coords.latitude, lng: p.coords.longitude }, { withCredentials: true }).catch(() => {}),
        () => {}, { timeout: 8000, maximumAge: 60000 });
      const tick = () => axios.get(`${API}/pogalong/squad`, { withCredentials: true }).then((r) => {
        drawSquad(r.data?.members);
        if (r.data?.sharing) push();
      }).catch(() => {});
      tick();
      timer = setInterval(tick, 120000);
    }
    return () => {
      if (timer) clearInterval(timer);
      if (marker && mapRef.current) mapRef.current.removeLayer(marker);
      if (squadLayer && mapRef.current) mapRef.current.removeLayer(squadLayer);
    };
  }, [driver]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the screen awake while AIR.POG is open (like a GPS app). Re-acquires
  // the lock when the tab becomes visible again (the OS drops it on blur/dim).
  useEffect(() => {
    let lock = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          lock = await navigator.wakeLock.request("screen");
          lock.addEventListener?.("release", () => { lock = null; });
        }
      } catch (e) { /* unsupported or denied */ }
    };
    const onVis = () => { if (document.visibilityState === "visible" && !lock) acquire(); };
    acquire();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try { lock && lock.release(); } catch (e) { /* noop */ }
    };
  }, []);

  // ---- 🌙 POG NEON NIGHT: saved theme wins; else auto-switch at true seasonal sunset, "?night" forces it ----
  useEffect(() => {
    const saved = localStorage.getItem("airpog_map_theme");
    // Seasonal solar approximation (mid-northern latitudes): sunset ~4:55pm midwinter → ~8:40pm midsummer.
    const doy = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    const wave = Math.sin(((doy - 80) / 365) * 2 * Math.PI);
    const sunsetH = 18.8 + 1.9 * wave;
    const sunriseH = 6.5 - 1.6 * wave;
    const hNow = new Date().getHours() + new Date().getMinutes() / 60;
    const autoNight = window.location.search.includes("night") || hNow >= sunsetH || hNow < sunriseH;
    const target = (saved && saved !== "day") ? saved : (!saved && autoNight ? "night" : null);
    if (!target || !mapRef.current) return;
    setMapTheme(target);
    tileRef.current?.setUrl(TILE_URLS[target] || TILE_URLS.day);
    const pane = mapRef.current.getPane("tilePane");
    if (pane) pane.style.filter = THEME_FILTERS[target] || "";
  }, []);


  // ---- live GPS tracking (separate effect so it ALWAYS re-registers on remount, incl. StrictMode) ----
  useEffect(() => {
    const onGeo = (p) => {
      if (driveRef.current.active) return; // simulation owns the camera/position
      const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
      // Heading: prefer GPS course when moving, else derive from how far we've moved.
      let hd = (p.coords.heading != null && !isNaN(p.coords.heading) && p.coords.speed > 0.5) ? p.coords.heading : null;
      const prev = prevPosRef.current;
      if (hd == null && prev && distMiles(prev, pos) > 0.003) hd = bearingDeg(prev, pos);
      if (hd != null) setHeading(hd);
      prevPosRef.current = pos;
      setUserPos(pos);
      // POG'WARP'd / exploring elsewhere? Don't yank the merchant radar back to real GPS.
      if (!deepLinkActiveRef.current) setSearchPos((cur) => (!cur || distMiles(cur, pos) > 0.12 ? pos : cur));
      // Flag IP/network-based fixes (very low accuracy) so we can ask the user to confirm their city.
      const acc = p.coords.accuracy;
      setGeoUncertain(acc != null && acc > 25000);
    };
    const onGeoErr = () => { ipFallback(); };

    // GPS denied/hung — drop the user in their approximate city from THEIR IP.
    // Done client-side (ipwho.is sees the visitor's real IP); the backend endpoint
    // sits behind a proxy and would only ever see the server's data-center IP.
    const ipFallback = async () => {
      const apply = (lat, lng) => {
        const pos = { lat: Number(lat), lng: Number(lng) };
        setUserPos((cur) => cur || pos);
        setSearchPos((cur) => cur || pos);
        setGeoUncertain(true);
      };
      // 1) client-side IP lookup (reflects the real visitor)
      try {
        const ctrl = new AbortController();
        const to = setTimeout(() => ctrl.abort(), 7000);
        const d = await fetch("https://ipwho.is/", { signal: ctrl.signal }).then((r) => r.json());
        clearTimeout(to);
        if (d && d.success && d.latitude != null && d.longitude != null) { apply(d.latitude, d.longitude); return; }
      } catch { /* try next */ }
      // 2) backend fallback (least accurate — server IP), better than nothing
      try {
        const r = await axios.get(`${API}/ip-location`, { timeout: 7000 });
        if (r.data?.lat != null) { apply(r.data.lat, r.data.lng); return; }
      } catch { /* fall through */ }
      setUserPos((cur) => cur || DEFAULT_POS);
      setSearchPos((cur) => cur || DEFAULT_POS);
    };

    const GEO_OPTS = { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 };
    navigator.geolocation?.getCurrentPosition(onGeo, onGeoErr, GEO_OPTS);
    // Keep the green dot following the driver in real time (fresh fixes for highway speed).
    if (navigator.geolocation?.watchPosition) {
      watchRef.current = navigator.geolocation.watchPosition(onGeo, () => {}, GEO_OPTS);
    }
    // Belt-and-suspenders: some mobile browsers throttle watchPosition — poll a fresh fix too.
    const poll = setInterval(() => {
      if (driveRef.current.active) return;
      navigator.geolocation?.getCurrentPosition(onGeo, () => {}, { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
    }, 4000);
    // ensure we always have a position even if geolocation hangs
    const t = setTimeout(() => { ipFallback(); }, 9000);
    return () => {
      clearTimeout(t);
      clearInterval(poll);
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    };
  }, []);

  // ---- load categories ----
  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data.categories)).catch(() => {});  }, []);

  // ---- peek the newest POG BLOG posts (community discovery on the map) ----
  useEffect(() => {
    axios.get(`${API}/blog`, { params: { limit: 3 } }).then((r) => setBlogPeek(r.data.slice(0, 3))).catch(() => {});
  }, []);

  // ---- deep link (/d/:bizId): center on a shared deal + open its panel ----
  useEffect(() => {
    if (!bizId) return;
    deepLinkActiveRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/business/${encodeURIComponent(bizId)}`);
        if (cancelled) return;
        const b = r.data;
        if (b.lat && b.lng) {
          const pos = { lat: b.lat, lng: b.lng };
          setSearchPos(pos);
          if (mapRef.current) mapRef.current.setView([b.lat, b.lng], 16);
        }
        axios.post(`${API}/events`, { business_id: b.id, type: "deal_view" }).catch(() => {});
        setAds({
          business_id: b.id,
          business_name: b.name,
          ads: b.ads || [],
          links: b.links || {},
          note: (b.ads && b.ads.length) ? "Live offers from this business." : "No active offers yet — tap to route here.",
          source: (b.ads && b.ads.length) ? "live" : "none",
          business: { id: b.id, name: b.name, lat: b.lat, lng: b.lng, category_id: b.category_id, icon: b.icon },
        });
      } catch (e) {
        setError("That deal link is no longer available.");
      }
    })();
    return () => { cancelled = true; };
  }, [bizId]);

  // ---- deep link (/pog/:pogId): open a shared (often private) POG + center on it ----
  useEffect(() => {
    if (!pogId) return;
    deepLinkActiveRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/personal-ads/${encodeURIComponent(pogId)}`);
        if (cancelled) return;
        const p = r.data;
        if (p.lat && p.lng) {
          setSearchPos({ lat: p.lat, lng: p.lng });
          if (mapRef.current) mapRef.current.setView([p.lat, p.lng], 16);
        }
        setPogDetail({
          id: p.id, name: p.title, lat: p.lat, lng: p.lng,
          is_sale: true, sale_type: p.sale_type, sale_detail: p.detail,
          sale_when: p.when_label, sale_tags: p.tags || [], address: p.address,
          location_mode: p.location_mode, delivery_note: p.delivery_note, delivery_radius_mi: p.delivery_radius_mi, contact: p.contact,
          ad_image_url: p.photo_url ? p.photo_url.replace(BACKEND_URL, "") : null,
          visibility: p.visibility, active: p.active,
        });
        pogTrack(p.id, "view");
      } catch (e) {
        setError("That POG link is no longer available.");
      }
    })();
    return () => { cancelled = true; };
  }, [pogId]);

  // ---- Plus checkout return: ?session_id=...&plus=1 (query, distinct from auth hash) ----
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("plus") !== "1" || !params.get("session_id")) return;
    const sid = params.get("session_id");
    window.history.replaceState({}, "", window.location.pathname);
    setPlusOpen(true);
    setPlusNotice({ type: "pending", text: "Confirming your Plus subscription…" });
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/consumer/checkout/status/${sid}`, { withCredentials: true });
        if (r.data.payment_status === "paid") {
          setPlusNotice({ type: "success", text: "🎉 AIR.POG Plus is active!" });
          if (refresh) refresh();
          return;
        }
        if (r.data.status === "expired") { setPlusNotice({ type: "error", text: "Payment session expired." }); return; }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 6) { setPlusNotice({ type: "error", text: "Still processing — give it a moment." }); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refresh]);

  const onFav = async (e, b) => {
    e.stopPropagation();
    if (!driver) { setPlusOpen(true); return; }
    try {
      setFavBusy(b.id);
      await toggleFavorite(b.id, { name: b.name || "", web: b.links?.web || b.links?.website || "", lat: b.lat ?? null, lng: b.lng ?? null });
    }
    catch (err) { if (err.response?.status === 402) setPlusOpen(true); }
    finally { setFavBusy(null); }
  };
  const isFav = (id) => !!driver?.favorites?.includes(id);

  const handlePlusCheckout = async () => {
    setPlusBusy(true);
    try { await startPlusCheckout(); }
    catch (e) { setPlusNotice({ type: "error", text: "Could not start checkout. Try again." }); setPlusBusy(false); }
  };

  const shareReferral = async () => {
    if (!driver?.referral_code) return;
    const url = `${window.location.origin}/?ref=${driver.referral_code}`;
    const text = "Join me on AIR.POG — find live local deals as you drive. We both get a free month of Plus:";
    try {
      if (navigator.share) await navigator.share({ title: "AIR.POG", text, url });
      else { await navigator.clipboard.writeText(url); setRefCopied(true); setTimeout(() => setRefCopied(false), 2200); }
    } catch (e) { /* cancelled */ }
  };

  // ---- place user marker + center ----
  // 📍 PIN CHOICE LOCKER — restyle the driver pin when the POG'R picks a new marker
  const [pinVer, setPinVer] = useState(0);
  useEffect(() => {
    const h = () => setPinVer((v) => v + 1);
    window.addEventListener("airpog:pin-style", h);
    return () => window.removeEventListener("airpog:pin-style", h);
  }, []);
  useEffect(() => {
    if (!userPos || !mapRef.current) return;
    const pinStyle = (() => { try { return localStorage.getItem("airpog_pin_style") || "pogzilla"; } catch { return "pogzilla"; } })();
    const pinArt = pinStyle === "classic" ? ""
      : (pinStyle === "mypog" && (localStorage.getItem("airpog_pin_pog") || ""))
        ? `<img src="${localStorage.getItem("airpog_pin_pog")}" alt="" style="position:absolute;left:50%;bottom:26px;transform:translateX(-50%);width:38px;height:38px;border-radius:50%;border:2px solid ${localStorage.getItem("airpog_pin_glow") || "#fbbf24"};background:#fff;box-shadow:0 0 12px ${localStorage.getItem("airpog_pin_glow") || "#fbbf24"},0 3px 6px rgba(2,6,23,.6);pointer-events:none;object-fit:cover;"/>${localStorage.getItem("airpog_pin_glow") === "#f472b6" ? '<span class="pin-shiny-spark s1">✦</span><span class="pin-shiny-spark s2">✦</span><span class="pin-shiny-spark s3">✦</span>' : ""}`
      : (pinStyle === "bolt" || pinStyle === "lemon" || pinStyle === "target")
        ? `<span style="position:absolute;left:50%;bottom:26px;transform:translateX(-50%);font-size:30px;filter:drop-shadow(0 3px 5px rgba(2,6,23,.55));pointer-events:none;">${pinStyle === "bolt" ? "⚡" : pinStyle === "lemon" ? "🍋" : "🎯"}</span>`
        : '<img src="/pogzilla-pin.png" alt="" data-testid="pogzilla-user-pin" style="position:absolute;left:50%;bottom:26px;transform:translateX(-50%);width:34px;height:47px;filter:drop-shadow(0 3px 5px rgba(2,6,23,.55));pointer-events:none;"/>';
    const icon = L.divIcon({
      className: "",
      html: `<div class="user-dot-wrap"><div class="user-cone-rot"><div class="user-cone"></div></div><div class="user-dot"></div>${pinArt}</div>`,
      iconSize: [52, 52], iconAnchor: [26, 26],
    });
    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng([userPos.lat, userPos.lng]);
      if (userMarkerRef.current._pinStyle !== pinStyle) { userMarkerRef.current.setIcon(icon); userMarkerRef.current._pinStyle = pinStyle; }
      // ✨ shiny pins leave a fading sparkle trail while moving
      if (pinStyle === "mypog" && localStorage.getItem("airpog_pin_glow") === "#f472b6") {
        const nowT = Date.now();
        if (!userMarkerRef.current._trailT || nowT - userMarkerRef.current._trailT > 900) {
          userMarkerRef.current._trailT = nowT;
          const tm = L.marker([userPos.lat, userPos.lng], { icon: L.divIcon({ className: "", html: '<span class="trail-spark">✦</span>', iconSize: [0, 0] }), keyboard: false, interactive: false }).addTo(mapRef.current);
          setTimeout(() => { try { mapRef.current?.removeLayer(tm); } catch (err) { /* ignore */ } }, 1300);
        }
      }
    } else {
      userMarkerRef.current = L.marker([userPos.lat, userPos.lng], { icon, keyboard: false }).addTo(mapRef.current);
      userMarkerRef.current._pinStyle = pinStyle;
    }
    // Point the heading cone the way the driver is moving.
    const el = userMarkerRef.current.getElement?.();
    // Smoothly glide the dot between GPS fixes instead of snapping (feels like Google Maps).
    // Disable the glide during map zoom so it doesn't drift, then restore.
    if (el && el.style.transition === "") el.style.transition = "transform 0.65s linear";
    const rot = el && el.querySelector(".user-cone-rot");
    if (rot) {
      if (heading == null) rot.style.opacity = "0";
      else { rot.style.opacity = "1"; rot.style.setProperty("--hd", `${heading}deg`); }
    }
    // While simulating or on a deep link, those flows control the camera.
    if (driveRef.current.active || deepLinkActiveRef.current) return;
    // Follow the driver in real time: first fix sets zoom, then smoothly pan to keep them centered.
    if (!followRef.current) return;
    if (firstViewRef.current) {
      mapRef.current.setView([userPos.lat, userPos.lng], 15);
      firstViewRef.current = false;
    } else {
      mapRef.current.panTo([userPos.lat, userPos.lng], { animate: true, duration: 0.5 });
    }
  }, [userPos, heading, pinVer]);

  // ---- fetch businesses (based on searchPos, not every driving micro-step) ----
  const fetchTokenRef = useRef(0);
  const fetchBusinesses = useCallback(async () => {
    if (!searchPos) return;
    const token = ++fetchTokenRef.current;  // cancel stale retries when params change
    setLoading(true);
    setError(null);
    const params = { lat: searchPos.lat, lng: searchPos.lng, radius };
    if (category) params.category = category;
    const stale = () => token !== fetchTokenRef.current;
    const wait = (ms) => new Promise((res) => setTimeout(res, ms));
    // Cold starts (idle backend waking up) can briefly 502/503/504/520/timeout.
    // Treat those as transient and keep retrying with backoff under a friendly
    // "waking up" state, so the first POG'r of the day never sees an error.
    const COLD = new Set([0, 408, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
    const isCold = (e) => !e.response || COLD.has(e.response?.status);
    const MAX = 6;
    const attempt = async (n) => {
      try {
        const r = await axios.get(`${API}/businesses`, { params, timeout: 20000 });
        if (stale()) return;
        if (r.data.length === 0 && n < 2) { await wait(2500); if (stale()) return; return attempt(n + 1); }
        setWarming(false);
        setBusinesses(Array.from(new Map(r.data.map((b) => [b.id, b])).values()));
        setError(r.data.length === 0 ? "No deals around you right now. Try a wider radius or another category." : null);
      } catch (e) {
        if (stale()) return;
        const cold = isCold(e);
        const max = cold ? MAX : 2;
        if (n < max) {
          if (cold) setWarming(true);
          await wait(cold ? Math.min(2000 + n * 1000, 5000) : 2500);
          if (stale()) return;
          return attempt(n + 1);
        }
        setWarming(false);
        setError(e.response?.data?.detail || "Could not load nearby businesses.");
        setBusinesses([]);
      }
    };
    await attempt(0);
    if (!stale()) { setLoading(false); setWarming(false); }
  }, [searchPos, radius, category]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  // ⚡ FULLY POG'D ZONE — inside a pre-loaded instant-data region?
  useEffect(() => {
    if (!searchPos) return;
    axios.get(`${API}/geo-coverage`, { params: { lat: searchPos.lat, lng: searchPos.lng } })
      .then((r) => {
        const covered = !!r.data.covered;
        setPoggedZone(covered);
        setZoneLoading(!covered && r.data.loading ? (r.data.label || "") : null);
        if (covered && !localStorage.getItem("airpog_pogdzone_toast")) {
          setZoneWelcome(true);
          try {
            const actx = new (window.AudioContext || window.webkitAudioContext)();
            [523.25, 659.25, 783.99].forEach((f, idx) => {
              const o = actx.createOscillator(); const g = actx.createGain();
              o.frequency.value = f; o.type = "sine"; o.connect(g); g.connect(actx.destination);
              const t0 = actx.currentTime + idx * 0.12;
              g.gain.setValueAtTime(0.0001, t0);
              g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.03);
              g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
              o.start(t0); o.stop(t0 + 0.45);
            });
          } catch { /* audio blocked before first gesture — fine */ }
          setTimeout(() => {
            setZoneWelcome(false);
            localStorage.setItem("airpog_pogdzone_toast", "1");
          }, 8000);
        }
      })
      .catch(() => setPoggedZone(false));
  }, [searchPos]);

  // 🎈 AUTO-FORGE spotlights near the radar center — badge their strip chips as deals.
  useEffect(() => {
    if (!searchPos) return;
    axios.get(`${API}/spotlights`, { params: { lat: searchPos.lat, lng: searchPos.lng } })
      .then((r) => {
        const m = new Map();
        for (const s of r.data.spotlights || []) {
          const cur = m.get(s.business_id);
          if (!cur || s.offer?.kind === "public_promo") m.set(s.business_id, s); // POG'R-spotted promos outrank
        }
        setSpotlightIds(m);
      })
      .catch(() => {});
  }, [searchPos]);

  // ⭐ POGMARK deal alerts — ping once when a bookmarked merchant has a fresh live deal.
  useEffect(() => {
    if (!businesses.length) return;
    try {
      const marked = new Set(JSON.parse(localStorage.getItem("airpog_pogodex_marks") || "[]"));
      if (!marked.size) return;
      const snap = JSON.parse(localStorage.getItem("airpog_marks_deals") || "{}");
      const dealt = businesses.filter((b) => marked.has(b.id) && (b.has_deals || b.featured));
      const fresh = dealt.find((b) => !snap[b.id]);
      localStorage.setItem("airpog_marks_deals", JSON.stringify(Object.fromEntries(dealt.map((b) => [b.id, true]))));
      if (fresh) {
        setMarkAlert(fresh);
        clearTimeout(markAlertT.current);
        markAlertT.current = setTimeout(() => setMarkAlert(null), 8000);
      }
    } catch { /* ignore */ }
  }, [businesses]);
  useEffect(() => () => clearTimeout(markAlertT.current), []);

  // ---- Plus deal alerts: ping when a deal is within ~1 mile while driving ----
  useEffect(() => {
    if (!driver?.is_plus || !driving) return;
    const cand = businesses.find((b) => (b.featured || b.has_deals) && b.distance_miles <= 1 && !alertedRef.current.has(b.id));
    if (!cand) return;
    alertedRef.current.add(cand.id);
    setDealAlert(cand);
    const t = setTimeout(() => setDealAlert(null), 6000);
    return () => clearTimeout(t);
  }, [businesses, driver, driving]);

  // ---- track impressions for displayed businesses (deduped per session) ----
  useEffect(() => {
    if (businesses.length === 0) return;
    const fresh = businesses.filter((b) => !impressionSet.current.has(b.id));
    if (fresh.length === 0) return;
    fresh.forEach((b) => impressionSet.current.add(b.id));
    axios.post(`${API}/events/batch`, { events: fresh.map((b) => ({ business_id: b.id, type: "impression" })) }).catch(() => {});
  }, [businesses]);

  // ---- render business markers ----
  useEffect(() => {
    const layer = bizLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    let marked = new Set();
    try { marked = new Set(JSON.parse(localStorage.getItem("airpog_pogodex_marks") || "[]")); } catch { /* ignore */ }
    const hotSet = new Set(hot.map((h) => h.business_id));
    businesses.forEach((b) => {
      const active = b.id === activeId;
      const w = active ? 34 : 29;
      const h = active ? 46 : 39;
      const icon = L.divIcon({ className: "biz-pin-wrap", html: bizPinHtml(b, active, marked.has(b.id), hotSet.has(b.id), hiringSet.has(b.id)), iconSize: [w, h], iconAnchor: [w / 2, h] });
      const m = L.marker([b.lat, b.lng], { icon, zIndexOffset: active ? 1000 : 0 }).addTo(layer);
      m.on("click", () => handleTabClick(b));
    });
  }, [businesses, activeId, markTick, hot]);

  // ---- one-time coach mark: nudge first-time POG'rs to tap a pin ----
  useEffect(() => {
    if (coachShownRef.current || businesses.length === 0) return;
    if (localStorage.getItem("airpog_coach_seen") || !localStorage.getItem("airpog_onboarded")) return;
    coachShownRef.current = true;
    setShowCoach(true);
    const t = setTimeout(() => { setShowCoach(false); localStorage.setItem("airpog_coach_seen", "1"); }, 6000);
    return () => clearTimeout(t);
  }, [businesses.length]);

  const dismissCoach = () => { setShowCoach(false); localStorage.setItem("airpog_coach_seen", "1"); };

  // ---- auto-scroll the strip — default LEFT → RIGHT; POG'RS flick to steer ----
  useEffect(() => {
    const el = stripEl || stripRef.current;
    if (!el || !el.isConnected || businesses.length === 0) return;
    let raf;
    let paused = false;
    let flickStart = null;
    // dir -1 = cards travel left→right (Sir Eric's default); 1 = right→left. Flicks persist.
    let dir = localStorage.getItem("airpog_strip_dir") === "-1" ? -1 : 1;
    let holdUntil = 0;
    const graceUntil = stripGraceRef.current;
    const onEnter = () => (paused = true);
    const onLeave = () => { if (Date.now() < holdUntil) return; paused = false; };
    let lastX = 0;
    let resumeT = null;
    let lastRoll = 0;
    let swallowClick = false;
    // ⏸💛 Hold ring — 5s countdown badge so POG'RS see the rail is paused on purpose.
    const removeRing = () => document.getElementById("rail-hold-ring")?.remove();
    const showRing = () => {
      removeRing();
      const rect = el.getBoundingClientRect();
      const b = document.createElement("div");
      b.id = "rail-hold-ring";
      b.setAttribute("data-testid", "rail-hold-ring");
      b.style.cssText = `position:fixed;top:${Math.max(4, rect.top - 34)}px;right:10px;z-index:1300;pointer-events:none;`;
      b.innerHTML = '<svg width="30" height="30" viewBox="0 0 30 30"><circle cx="15" cy="15" r="13" fill="rgba(15,23,42,.92)"/><circle cx="15" cy="15" r="13" fill="none" stroke="#fbbf24" stroke-width="2.5" stroke-dasharray="81.7" stroke-dashoffset="0" transform="rotate(-90 15 15)" style="transition:stroke-dashoffset 5s linear"/><text x="15" y="20" text-anchor="middle" font-size="12">⏸</text></svg>';
      document.body.appendChild(b);
      requestAnimationFrame(() => { const c = b.querySelectorAll("circle")[1]; if (c) c.style.strokeDashoffset = "81.7"; });
    };
    const onDown = (e) => {
      clearTimeout(resumeT);
      removeRing();
      // First touch on a ROLLING rail = stop only — swallow the click so cards don't fire.
      swallowClick = e.pointerType !== "mouse" && Date.now() - lastRoll < 250;
      paused = true;
      flickStart = { x: e.clientX, t: Date.now(), sl: el.scrollLeft, pid: e.pointerId };
      lastX = e.clientX;
    };
    const onClickCap = (ev) => {
      if (swallowClick) { ev.stopPropagation(); ev.preventDefault(); swallowClick = false; }
    };
    const onMove = (e) => { if (flickStart && e.pointerId === flickStart.pid) lastX = e.clientX; };
    // Window-level release: catches drags ending over overlays AND touch pointercancel (native scroll hijack).
    const onUp = (e) => {
      if (!flickStart || e.pointerId !== flickStart.pid) return;
      const endX = e.type === "pointerup" ? e.clientX : lastX;
      let dx = endX - flickStart.x;
      const sdx = flickStart.sl - el.scrollLeft; // native scroll fallback: finger right ⇒ scrollLeft drops ⇒ sdx > 0
      if (Math.abs(sdx) > Math.abs(dx)) dx = sdx;
      const dt = Date.now() - flickStart.t;
      if (Math.abs(dx) > 40 && dt < 600) {
        dir = dx > 0 ? -1 : 1;
        localStorage.setItem("airpog_strip_dir", String(dir));
      }
      flickStart = null;
      if (e.pointerType !== "mouse") {
        // ⏸ Touch = 5-second hold so POG'RS can tap tiles/hearts, then the rail rolls on same direction.
        clearTimeout(resumeT);
        holdUntil = Date.now() + 5000;
        showRing();
        resumeT = setTimeout(() => { holdUntil = 0; paused = false; removeRing(); }, 5000);
        setTimeout(() => { swallowClick = false; }, 350); // click (if any) lands right after pointerup
      }
    };
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("click", onClickCap, true);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    el.addEventListener("pointerleave", onLeave);
    // FULL STEAM AHEAD: scale speed with fleet size — TURBO kicks in at 300+ merchants.
    const speed = Math.min(5, Math.max(0.6, businesses.length * 0.011 + (businesses.length >= 300 ? 1.2 : 0)));
    const step = () => {
      if (!paused && el && Date.now() >= graceUntil) {
        lastRoll = Date.now();
        if (layout === "horizontal" || layout === "double") {
          el.scrollLeft += speed * dir;
          if (dir > 0 && el.scrollLeft >= el.scrollWidth - el.clientWidth - 1) el.scrollLeft = 0;
          if (dir < 0 && el.scrollLeft <= 1) el.scrollLeft = el.scrollWidth - el.clientWidth - 1;
        } else {
          el.scrollTop += speed;
          if (el.scrollTop >= el.scrollHeight - el.clientHeight - 1) el.scrollTop = 0;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("click", onClickCap, true);
      clearTimeout(resumeT);
      removeRing();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [businesses, layout, stripEl]);

  // ---- single tap = route, double tap = ads ----
  const routeTo = async (b) => {
    if (b?.lat == null) { setError("This spot has no pin to route to."); return; }
    if (!userPos) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}`, "_blank");
      return;
    }
    setActiveId(b.id);
    setRouting(true);
    setNav(null);
    axios.post(`${API}/events`, { business_id: b.id, type: "route" }).catch(() => {});
    try {
      const r = await axios.get(`${API}/route`, {
        params: { from_lat: userPos.lat, from_lng: userPos.lng, to_lat: b.lat, to_lng: b.lng },
      });
      routeLayerRef.current.clearLayers();
      const line = L.polyline(r.data.geometry, {
        color: "#38BDF8", weight: 6, opacity: 0.95, className: "route-glow",
      }).addTo(routeLayerRef.current);
      mapRef.current.fitBounds(line.getBounds(), { padding: [80, 80] });
      setNav({ ...r.data, business: b });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not calculate a route.");
    } finally {
      setRouting(false);
    }
  };

  const showAds = async (b) => {
    axios.post(`${API}/events`, { business_id: b.id, type: "deal_view" }).catch(() => {});
    setAdsLoading(true);
    setDealOpen(false);
    setDealForm({ title: "", detail: "", discount: "", expires: "Limited time" });
    setAds({ business_name: b.name, ads: [], loading: true, business: b });
    try {
      const r = await axios.get(`${API}/businesses/${encodeURIComponent(b.id)}/ads`, {
        params: { name: b.name, category: b.category_id },
      });
      setAds({ ...r.data, business: b });
    } catch (e) {
      setAds(null);
      setError("Could not load offers.");
    } finally {
      setAdsLoading(false);
    }
  };

  // Auto-minimize the POG ADS panel after a while of inactivity (was 9s — closed on people mid-read).
  useEffect(() => {
    if (!ads || ads.loading || adsLoading) return;
    const t = setTimeout(() => {
      // Never yank the panel while the POG PING form is open (typing a note / paying for a GUARANTEED run).
      if (document.querySelector('[data-testid="pog-ping-form"]')) return;
      setAds(null);
    }, 30000);
    return () => clearTimeout(t);
  }, [ads, adsLoading]);


  const startRedeem = async (deal, bizIdOverride) => {
    const bizId = bizIdOverride || ads?.business_id || ads?.business?.id;
    if (!bizId) return;
    setRedeemBusy(true);
    try {
      const r = await axios.post(`${API}/redeem`, { business_id: bizId, deal_id: deal.id }, { withCredentials: true });
      setRedeem(r.data);
      if (r.data.awarded_pog) setTimeout(() => showCaught(r.data.awarded_pog), 700);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't open this deal. Please try again.");
    } finally {
      setRedeemBusy(false);
    }
  };

  // tick the redemption clock once per second while the redeem screen is open
  useEffect(() => {
    if (!redeem) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [redeem]);

  // ☁️ ascension confetti — celebrate the moment staff confirms the redemption
  useEffect(() => {
    if (redeem?.redeemed) {
      confetti({ particleCount: 130, spread: 110, origin: { y: 0.3 }, colors: ["#38bdf8", "#fbbf24", "#ffffff", "#a5f3fc"] });
    }
  }, [redeem?.redeemed]);

  // poll redemption status so "Redeemed" flips live when staff confirms
  useEffect(() => {
    if (!redeem || redeem.redeemed) return;
    const t = setInterval(() => {
      axios.get(`${API}/redeem/${redeem.token}`)
        .then((r) => { if (r.data.redeemed) setRedeem((cur) => (cur && cur.token === r.data.token ? { ...cur, redeemed: true, redeemed_at: r.data.redeemed_at } : cur)); })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [redeem]);

  const checkIn = async (biz) => {
    if (!biz?.id) return;
    if (!driver) { loginGoogle?.(); return; }
    if (!userPos) { setCheckinMsg({ err: "We need your location to check in." }); return; }
    setCheckinBusy(true); setCheckinMsg(null);
    try {
      const r = await axios.post(`${API}/checkin/${encodeURIComponent(biz.id)}`, { lat: userPos.lat, lng: userPos.lng }, { withCredentials: true });
      if (r.data.already) setCheckinMsg({ text: "✓ Already checked in here today!" });
      else if (r.data.capped) setCheckinMsg({ text: "Daily check-in limit reached — come back tomorrow!" });
      else { setCheckinMsg({ ok: true, text: `🎯 +${r.data.points_earned} POG BUCKS for checking in!`, sub: "🥞 Stacks on top of their rewards — you keep both!" }); refresh?.(); }
    } catch (e) {
      setCheckinMsg({ err: e.response?.data?.detail || "Could not check in." });
    } finally {
      setCheckinBusy(false);
    }
  };

  const showVisitQr = async (biz) => {
    if (!biz?.id) return;
    if (!driver) { loginGoogle?.(); return; }
    setVisitQrBusy(true);
    try {
      const r = await axios.post(`${API}/consumer/redeem-token`, {
        business_id: biz.id,
        business_name: ads?.business_name || biz.name || "",
        origin: window.location.origin,
      }, { withCredentials: true });
      setVisitQr({ ...r.data, business_name: ads?.business_name || biz.name });
      setAds(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't create your POG BUCKZ code. Please try again.");
    } finally {
      setVisitQrBusy(false);
    }
  };

  // 🎉 Fire a POGZILLA celebration the moment a logged-in POG'R arrives at the open shop.
  useEffect(() => {
    const b = ads?.business;
    if (!driver || !userPos || !b?.lat || !b?.lng || b.is_sale) return;
    const bid = b.id || ads.business_id;
    if (distMiles(userPos, b) <= 0.12 && arrivalCelebrated !== bid) {
      setArrivalCelebrated(bid);
      setShowArrival(true);
      pogConfetti();
    }
  }, [ads, userPos, driver, arrivalCelebrated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!driver) return;
    axios.get(`${API}/dm/unread`, { withCredentials: true }).then((r) => setDmUnread(r.data.unread || 0)).catch(() => {});
  }, [driver]);

  const sendDm = async () => {
    const body = dmText.trim();
    if (!body || !dmCompose) return;
    setDmBusy(true);
    try {
      await axios.post(`${API}/dm/send`, { sale_id: dmCompose.sale_id, body }, { withCredentials: true });
      setDmSent(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't send your request. Try again.");
    } finally { setDmBusy(false); }
  };

  const shareArrivalMoment = async () => {
    setSharingMoment(true); setShareMsg("");
    try {
      const deal = ads?.ads?.[0] || {};
      const res = await sharePogMoment({
        businessName: ads?.business_name,
        dealTitle: deal.title,
        discount: deal.discount,
        reward: 30,
      });
      if (res === "aborted") { setSharingMoment(false); return; }
      let msg = res === "downloaded" ? "POG card saved & caption copied — paste it anywhere! 🛹" : "Shared! 🛹";
      try {
        const r = await axios.post(`${API}/consumer/share-reward`, {}, { withCredentials: true });
        if (r.data?.awarded > 0) msg = `Shared! +${r.data.awarded} 🪙 POG BUCKZ for spreading the POG! 🛹`;
      } catch { /* reward is a bonus; ignore failures */ }
      setShareMsg(msg);
    } catch {
      setShareMsg("Couldn't build your POG card. Try again.");
    } finally { setSharingMoment(false); }
  };

  const postDeal = async () => {
    if (!ads?.business) return;
    if (!dealForm.title.trim() || !dealForm.discount.trim()) {
      setError("Add at least a title and a discount.");
      return;
    }
    setPosting(true);
    try {
      await axios.post(`${API}/businesses/${encodeURIComponent(ads.business.id)}/deals`, {
        business_name: ads.business.name,
        title: dealForm.title,
        detail: dealForm.detail || "In-store offer",
        discount: dealForm.discount,
        expires: dealForm.expires || "Limited time",
      });
      const b = ads.business;
      setDealOpen(false);
      setDealForm({ title: "", detail: "", discount: "", expires: "Limited time" });
      await showAds(b);
    } catch (e) {
      setError("Could not post the deal.");
    } finally {
      setPosting(false);
    }
  };

  const handleTabClick = (b) => {
    if (showCoach) dismissCoach();
    // One tap always opens the POG ADS panel; routing happens from a second tap
    // inside the panel ("Route to this deal" / "Route here"). Panel has an X and
    // auto-minimizes after a few seconds.
    if (b.is_sale) { setPogDetail(b); pogTrack(b.id, "view"); return; }
    if (b.ad_headline && b.ad_image_url) { openPogAd(b); return; }
    showAds(b);
  };

  // POG AD — expanded merchant ad with 🎯 route / 🔗 POG Link / Ⓐ app / 🌐 web buttons.
  const openPogAd = (b) => {
    setActiveId(b.id);
    setPogAd({ biz: b, bucks: 0, deals: [] });
    axios.post(`${API}/pog-ads/${encodeURIComponent(b.id)}/tap`, {}, { withCredentials: true })
      .then((r) => {
        if (r.data.bucks_awarded) {
          setPogAd((p) => (p && p.biz.id === b.id ? { ...p, bucks: r.data.bucks_awarded } : p));
          refresh?.();
        }
      })
      .catch(() => {});
    if (b.has_deals) {
      axios.get(`${API}/businesses/${encodeURIComponent(b.id)}/ads`, { params: { name: b.name, category: b.category_id } })
        .then((r) => {
          const live = (r.data.ads || []).filter((a) => a.live && a.id);
          setPogAd((p) => (p && p.biz.id === b.id ? { ...p, deals: live } : p));
        })
        .catch(() => {});
    }
  };

  // Auto-minimize the POG AD panel after a few seconds, like the deals panel.
  useEffect(() => {
    if (!pogAd) return;
    const t = setTimeout(() => setPogAd(null), 12000);
    return () => clearTimeout(t);
  }, [pogAd]);

  const toggleMapTheme = () => {
    setMapTheme((t) => {
      const next = THEME_CYCLE[t] || "day";
      if (tileRef.current) tileRef.current.setUrl(TILE_URLS[next]);
      // Theme washes: brighten night tiles; purple-dusk wash for TWILIGHT mode.
      const pane = mapRef.current?.getPane("tilePane");
      if (pane) pane.style.filter = THEME_FILTERS[next] || "";
      localStorage.setItem("airpog_map_theme", next);
      return next;
    });
  };

  // 🌙 quick-toggle from the strip's moon button — cycles the same theme wheel
  useEffect(() => {
    const h = () => toggleMapTheme();
    window.addEventListener("airpog:cycle-theme", h);
    return () => window.removeEventListener("airpog:cycle-theme", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Personal POGs (consumer-posted garage/estate sales, parties, events...) ----
  const localDatetimeDefault = (hoursAhead) => {
    const d = new Date(Date.now() + hoursAhead * 3600 * 1000 - new Date().getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  };

  const openPostPog = () => {
    setMoreOpen(false);
    if (!driver) { setPlusOpen(true); return; }
    setPogShare(null);
    setPogPhotoFile(null);
    setPogForm({ type: "Garage Sale", custom: "", visibility: "public", location_mode: "exact", delivery_radius: 30, delivery_note: "", contact: "", title: "", detail: "", tags: "", ends: localDatetimeDefault(4), err: null });
    setPogOpen(true);
  };

  // Crowd-sourced business pins: drivers drop a pin for a spot AIR.POG is missing.
  const openAddBiz = () => {
    setMoreOpen(false);
    if (!driver) { loginGoogle?.(); return; }
    setPlacingBiz(true);
  };
  const confirmPlaceBiz = () => {
    const center = mapRef.current ? mapRef.current.getCenter() : (userPos || DEFAULT_POS);
    setPlacingBiz(false);
    setAddBizDone(null);
    setAddBizForm({ name: "", category_id: "", address: "", note: "", lat: center.lat, lng: center.lng, err: null });
    setAddBizOpen(true);
  };
  const submitAddBiz = async () => {
    if (!addBizForm) return;
    if (!addBizForm.name.trim()) { setAddBizForm((f) => ({ ...f, err: "What's the business called?" })); return; }
    setAddBizBusy(true);
    try {
      const r = await axios.post(`${API}/community/business`, {
        name: addBizForm.name.trim(),
        category_id: addBizForm.category_id || "other",
        address: addBizForm.address.trim(),
        note: addBizForm.note.trim(),
        lat: addBizForm.lat, lng: addBizForm.lng,
        ends_on: (addBizForm.category_id === "yard_sale" && addBizForm.ends_on) ? addBizForm.ends_on : null,
      }, { withCredentials: true });
      setAddBizDone(r.data);
      pogConfetti();
      setSearchPos((p) => (p ? { ...p } : p)); // refetch nearby so the new pin appears
      refresh?.();
    } catch (e) {
      setAddBizForm((f) => ({ ...f, err: e.response?.data?.detail || "Couldn't add that spot. Please try again." }));
    } finally { setAddBizBusy(false); }
  };

  // Crowd verification: 👍/👎 a community-added spot (one vote per device per spot).
  const hasVoted = (id) => !!voted[id] || !!localStorage.getItem(`airpog_bizvote_${id}`);
  const voteBiz = async (e, b, dir) => {
    e.stopPropagation();
    if (!driver) { loginGoogle?.(); return; }
    if (hasVoted(b.id)) return;
    localStorage.setItem(`airpog_bizvote_${b.id}`, dir);
    setVoted((v) => ({ ...v, [b.id]: dir }));
    try {
      const r = await axios.post(`${API}/community/business/${b.id}/vote`, { vote: dir }, { withCredentials: true });
      if (dir === "up") pogConfetti();
      refresh?.();
      if (r.data?.hidden) setSearchPos((p) => (p ? { ...p } : p)); // refetch so a downvoted-out spot disappears
    } catch { /* keep optimistic state */ }
  };

  const loadMyPogs = useCallback(async () => {
    if (!driver) { setMyPogs([]); return; }
    try {
      const r = await axios.get(`${API}/personal-ads/mine`, { withCredentials: true });
      setMyPogs(r.data);
      // "POG is heating up" — celebrate when a POG crosses a view milestone (once each).
      const MS = [1000, 500, 250, 100, 50, 25];
      for (const p of r.data) {
        const v = p.views || 0;
        const hit = MS.find((m) => v >= m);
        if (!hit) continue;
        const key = `airpog_pogms_${p.id}`;
        const seen = parseInt(localStorage.getItem(key) || "0", 10);
        if (hit > seen) {
          localStorage.setItem(key, String(hit));
          setMilestoneToast({ title: p.title, views: v, milestone: hit });
        }
      }
    } catch { setMyPogs([]); }
  }, [driver]);

  useEffect(() => { loadMyPogs(); }, [loadMyPogs]);

  // auto-dismiss the milestone toast
  useEffect(() => {
    if (!milestoneToast) return;
    const t = setTimeout(() => setMilestoneToast(null), 7000);
    return () => clearTimeout(t);
  }, [milestoneToast]);

  // When the location fix is imprecise, reverse-geocode it so we can name the wrong city.
  useEffect(() => {
    if (!geoUncertain || !userPos) { return; }
    let cancel = false;
    axios.get(`${API}/reverse-geocode`, { params: { lat: userPos.lat, lng: userPos.lng } })
      .then((r) => { if (!cancel) setDetectedCity(r.data?.city || ""); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [geoUncertain, userPos]);

  const submitPog = async () => {
    if (!pogForm) return;
    const type = pogForm.type === "__custom__" ? (pogForm.custom.trim() || "Personal POG") : pogForm.type;
    if (!pogForm.title.trim()) { setPogForm((f) => ({ ...f, err: "Give your POG a title." })); return; }
    if (!pogForm.ends) { setPogForm((f) => ({ ...f, err: "Pick when it ends." })); return; }
    const center = mapRef.current ? mapRef.current.getCenter() : (userPos || DEFAULT_POS);
    setPogBusy(true);
    try {
      const r = await axios.post(`${API}/personal-ads`, {
        title: pogForm.title.trim(), sale_type: type, visibility: pogForm.visibility,
        location_mode: pogForm.location_mode,
        delivery_radius_mi: pogForm.delivery_radius,
        delivery_note: pogForm.delivery_note.trim(),
        contact: pogForm.contact.trim(),
        lat: center.lat, lng: center.lng, detail: pogForm.detail.trim(),
        ends_at: new Date(pogForm.ends).toISOString(),
        tags: pogForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      }, { withCredentials: true });
      const created = r.data;
      if (pogPhotoFile) {
        const fd = new FormData(); fd.append("file", pogPhotoFile);
        try { const pr = await axios.post(`${API}/personal-ads/${created.id}/photo`, fd, { withCredentials: true }); created.photo_url = pr.data.photo_url; } catch { /* photo optional */ }
      }
      setPogShare(created);
      pogConfetti();
      if (!localStorage.getItem("airpog_first_pog")) {
        localStorage.setItem("airpog_first_pog", "1");
        confetti({ particleCount: 220, spread: 130, origin: { y: 0.3 }, colors: ["#FDE047", "#34D399", "#E879F9", "#22D3EE"] });
        setTimeout(() => toast.success("🐲 POGZILLA CHEERS FOR YOU! Your very FIRST POG is LIVE — the whole neighborhood can find you now! ⚡", { duration: 9000 }), 400);
      }
      if (created.awarded_pog) setTimeout(() => showCaught(created.awarded_pog), 700);
      setSearchPos((p) => (p ? { ...p } : p)); // retrigger nearby refetch so it appears
      loadMyPogs();
    } catch (e) {
      setPogForm((f) => ({ ...f, err: e.response?.data?.detail || "Couldn't post your POG. Please try again." }));
    } finally { setPogBusy(false); }
  };

  const deletePog = async (id) => {
    try {
      await axios.delete(`${API}/personal-ads/${id}`, { withCredentials: true });
      setMyPogs((m) => m.filter((p) => p.id !== id));
      setSearchPos((p) => (p ? { ...p } : p));
    } catch { /* ignore */ }
  };

  const sharePog = async (id) => {
    const url = `${window.location.origin}/api/pog/${id}`;
    try {
      if (navigator.share) await navigator.share({ title: "AIR.POG", text: "📍 Check out my POG on AIR.POG:", url });
      else { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2200); }
    } catch { /* cancelled */ }
  };

  const pogTrack = (id, kind) => {
    if (!id) return;
    if (kind === "view") {
      const key = `airpog_pogv_${id}`;
      if (localStorage.getItem(key)) return; // one view per device
      localStorage.setItem(key, "1");
    }
    axios.post(`${API}/personal-ads/${id}/${kind}`).catch(() => {});
  };

  const inviteBusiness = async (b, name) => {
    const link = `${window.location.origin}/business?claim=${encodeURIComponent(b.id)}&name=${encodeURIComponent(name || "")}&lat=${b.lat}&lng=${b.lng}`;
    const text = `Hey! ${name || "Your business"} is already on AIR.POG — drivers nearby see it on the live map. Claim it FREE (add your logo & deals): ${link}`;
    try {
      if (navigator.share) await navigator.share({ title: "AIR.POG — claim your business", text, url: link });
      else window.location.href = `mailto:?subject=${encodeURIComponent("You're on AIR.POG — claim your business free")}&body=${encodeURIComponent(text)}`;
    } catch { /* cancelled */ }
  };

  const recenter = () => {
    deepLinkActiveRef.current = false;
    followRef.current = true;
    setExploreActive(false);
    setExploreLabel("");
    if (userPos) setSearchPos(userPos);
    if (userPos && mapRef.current) mapRef.current.setView([userPos.lat, userPos.lng], 16);
  };

  // ---- "Explore anywhere": scan deals around a tapped/searched spot without
  // losing your real GPS position ("Back to me" returns you). ----
  const setExplore = (lat, lng, label) => {
    if (driveRef.current.active) stopDrive();
    deepLinkActiveRef.current = true; // suppress GPS auto-follow snapping us back
    followRef.current = false;
    const pos = { lat, lng };
    setSearchPos(pos);
    setBusinesses([]); // clear the old area's pins immediately so the switch is visible
    setExploreActive(true);
    setExploreLabel(label || "this spot");
    if (mapRef.current) mapRef.current.setView([lat, lng], travelMode === "fly" ? 12 : 15);
    setLocOpen(false); setLocResults([]); setLocQuery(""); setError(null);
    setGeoUncertain(false); // user has set a deliberate location
    window.dispatchEvent(new CustomEvent("airpog:warped", { detail: { lat, lng, label } })); // 🔍 warp-then-find
  };
  const setExploreRef = useRef(setExplore);
  setExploreRef.current = setExplore;

  const changeMode = (m) => {
    if (!TRAVEL_MODES[m]) return;
    setTravelMode(m);
    localStorage.setItem("airpog_travel_mode", m);
    setRadius(TRAVEL_MODES[m].radius);
    // Heading somewhere? Fly/Transit are about a destination — open the picker.
    if ((m === "fly" || m === "transit") && !exploreActive) setLocOpen(true);
  };

  const toggleTrucker = () => {
    setTruckerMode((v) => {
      const next = !v;
      localStorage.setItem("airpog_trucker_mode", next ? "1" : "0");
      if (!next && category && categories.find((c) => c.id === category)?.trucker) setCategory(null);
      return next;
    });
  };

  // ---- Navigate with the driver's preferred GPS app (Google / Waze / Apple) ----
  const openNav = (app, b, remember) => {
    const dest = b || gpsSheet;
    if (!dest) return;
    if (remember) localStorage.setItem("airpog_gps_pref", app);
    window.open(GPS_APPS[app].url(dest.lat, dest.lng), "_blank", "noopener");
    setGpsSheet(null);
  };

  const navigateExternal = (b) => {
    if (!b) return;
    const pref = localStorage.getItem("airpog_gps_pref");
    if (pref && GPS_APPS[pref]) openNav(pref, b, false);
    else setGpsSheet(b);
  };

  const openLink = (bizId, kind, url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
    axios.post(`${API}/events`, { business_id: bizId, type: "link_click" }).catch(() => {});
  };

  const shareDeal = async (b) => {
    if (!b) return;
    const url = `${window.location.origin}/api/share/${encodeURIComponent(b.id)}`;
    const text = `🔥 ${b.name} has a deal on AIR.POG — tap to find it near you:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "AIR.POG Deal", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }
    } catch (e) { /* user cancelled share */ }
  };

  // ---- Ask AIR.POG (natural-language AI search) ----
  const askAirpog = async (override) => {
    const q = (typeof override === "string" ? override : askQuery).trim();
    if (!q) return;
    if (typeof override === "string") setAskQuery(override);
    const pos = userPos || DEFAULT_POS;
    setAskLoading(true);
    setAskResult(null);
    try {
      const r = await axios.post(`${API}/ask`, { query: q, lat: pos.lat, lng: pos.lng, radius: Math.max(radius, 3) });
      setAskResult(r.data);
    } catch (e) {
      setAskResult({ answer: e.response?.data?.detail || "AIR.POG couldn't answer that right now. Please try again.", businesses: [] });
    } finally {
      setAskLoading(false);
    }
  };

  const routeFromAsk = (b) => {
    setAskOpen(false);
    if (mapRef.current) mapRef.current.setView([b.lat, b.lng], 15);
    routeTo(b);
  };

  // ---- manual location (search / GPS retry) ----
  const applyLocation = (lat, lng) => {
    if (driveRef.current.active) stopDrive();
    const pos = { lat, lng };
    setUserPos(pos);
    setSearchPos(pos);
    setExploreActive(false);
    setExploreLabel("");
    if (mapRef.current) mapRef.current.setView([lat, lng], 15);
    setLocOpen(false);
    setLocResults([]);
    setLocQuery("");
    setError(null);
  };

  const requestGeo = () => {
    if (!navigator.geolocation) {
      setError("This browser can't access GPS. Try searching your location instead.");
      return;
    }
    setLocSearching(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLocSearching(false); applyLocation(p.coords.latitude, p.coords.longitude); },
      () => { setLocSearching(false); setError("Couldn't get GPS. Allow location in your browser, or search your location below."); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const doGeoSearch = async () => {
    if (!locQuery.trim()) return;
    setLocSearching(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/geocode`, { params: { q: locQuery } });
      setLocResults(r.data);
      if (r.data.length === 0) setError("No matching place found. Try a city or full address.");
    } catch (e) {
      setError("Location search failed. Please try again.");
    } finally {
      setLocSearching(false);
    }
  };

  // Keyword/cuisine search (e.g. "tacos", "mexican") — hand off to the AIR.POG AI finder.
  const searchAsDeals = (q) => {
    const query = ((typeof q === "string" ? q : locQuery) || "").trim();
    if (!query) return;
    setLocOpen(false);
    setLocResults([]);
    setError(null);
    setAskOpen(true);
    setAskQuery(query);
    askAirpog(query);
  };

  const clearRoute = () => {
    routeLayerRef.current?.clearLayers();
    setNav(null);
    setActiveId(null);
    recenter();
  };

  // ---- driving simulation helpers ----
  const offsetPoint = (pos, miles, bearingDeg) => {
    const R = 3958.8;
    const br = (bearingDeg * Math.PI) / 180;
    const lat1 = (pos.lat * Math.PI) / 180;
    const lng1 = (pos.lng * Math.PI) / 180;
    const dr = miles / R;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(br));
    const lng2 = lng1 + Math.atan2(Math.sin(br) * Math.sin(dr) * Math.cos(lat1), Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2));
    return { lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI };
  };

  const distMiles = (a, b) => {
    const R = 3958.8;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  const fetchLeg = async (from) => {
    for (let attempt = 0; attempt < 4; attempt++) {
      const dest = offsetPoint(from, 0.8 + Math.random() * 0.6, Math.random() * 360);
      try {
        const r = await axios.get(`${API}/route`, {
          params: { from_lat: from.lat, from_lng: from.lng, to_lat: dest.lat, to_lng: dest.lng },
        });
        if (r.data.geometry && r.data.geometry.length > 5) return r.data.geometry;
      } catch (e) { /* try another bearing */ }
    }
    return null;
  };

  const drawCruise = (geo) => {
    cruiseLayerRef.current.clearLayers();
    L.polyline(geo, { color: "#10B981", weight: 5, opacity: 0.7, dashArray: "2 10", className: "route-glow" }).addTo(cruiseLayerRef.current);
  };

  const stepDrive = () => {
    const d = driveRef.current;
    if (!d.active || !mapRef.current) return;
    if (d.idx >= d.path.length) {
      const last = d.path[d.path.length - 1];
      const from = last ? { lat: last[0], lng: last[1] } : null;
      if (from) {
        fetchLeg(from).then((geo) => {
          if (!driveRef.current.active) return;
          if (geo) { driveRef.current.path = geo; driveRef.current.idx = 0; drawCruise(geo); stepDrive(); }
          else stopDrive();
        });
      } else stopDrive();
      return;
    }
    const [lat, lng] = d.path[d.idx];
    const pos = { lat, lng };
    setUserPos(pos);
    mapRef.current.panTo([lat, lng], { animate: true, duration: 0.12 });
    if (!d.lastSearch || distMiles(d.lastSearch, pos) > 0.12) {
      d.lastSearch = pos;
      setSearchPos(pos);
    }
    d.idx += 1;
    d.timer = setTimeout(stepDrive, 140);
  };

  const startDrive = async () => {
    if (!userPos || driving) return;
    setError(null);
    setDriving(true);
    driveRef.current.active = true;
    mapRef.current.setZoom(16);
    const geo = await fetchLeg(userPos);
    if (!geo || !driveRef.current.active) {
      if (!geo) setError("Couldn't start a drive here. Try a more built-up area.");
      stopDrive();
      return;
    }
    driveRef.current.path = geo;
    driveRef.current.idx = 0;
    driveRef.current.lastSearch = userPos;
    drawCruise(geo);
    stepDrive();
  };

  const stopDrive = () => {
    const d = driveRef.current;
    d.active = false;
    if (d.timer) clearTimeout(d.timer);
    d.timer = null;
    d.path = [];
    d.idx = 0;
    cruiseLayerRef.current?.clearLayers();
    setDriving(false);
  };

  const toggleDrive = () => { driving ? stopDrive() : startDrive(); };

  // 🦖 Unleash the DYNO STAMPEDE — a herd of POGZILLAs charge across + confetti.
  const pogzillaTimer = useRef(null);
  const unleashPogzilla = () => {
    setMoreOpen(false);
    setPogzilla(true);
    pogConfetti();
    if (pogzillaTimer.current) clearTimeout(pogzillaTimer.current);
    pogzillaTimer.current = setTimeout(() => setPogzilla(false), 4700);
  };
  useEffect(() => () => { if (pogzillaTimer.current) clearTimeout(pogzillaTimer.current); }, []);

  // 🎈 AIR.POG BALLOON FEST — balloons drift up the map. Auto-launches every October.
  const [balloons, setBalloons] = useState(false);
  const balloonTimer = useRef(null);
  const launchBalloons = () => {
    setMoreOpen(false);
    setBalloons(true);
    pogConfetti();
    if (balloonTimer.current) clearTimeout(balloonTimer.current);
    balloonTimer.current = setTimeout(() => setBalloons(false), 15000);
  };
  useEffect(() => {
    if (new Date().getMonth() !== 9) return; // October only
    try {
      if (sessionStorage.getItem("airpog_balloonfest_seen")) return;
      sessionStorage.setItem("airpog_balloonfest_seen", "1");
    } catch { /* ignore */ }
    const t = setTimeout(launchBalloons, 3500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (balloonTimer.current) clearTimeout(balloonTimer.current); }, []);

  // 🎈 Countdown teaser: shows Sep 15 → Oct 1 every year ("?fest_tease" forces a preview).
  const balloonCountdown = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 9, 1);
    const days = Math.max(1, Math.ceil((start - now) / 86400000));
    if (window.location.search.includes("fest_tease")) return days;
    const tease = new Date(now.getFullYear(), 8, 15);
    return now >= tease && now < start ? days : null;
  }, []);
  const [teaserGone, setTeaserGone] = useState(() => {
    try { return sessionStorage.getItem("airpog_fest_tease_gone") === "1"; } catch { return false; }
  });
  const dismissTeaser = () => {
    try { sessionStorage.setItem("airpog_fest_tease_gone", "1"); } catch { /* ignore */ }
    setTeaserGone(true);
  };

  // 🎉 Celebrate catching an AIR.POG (from posting a POG or redeeming a deal).
  const caughtTimer = useRef(null);
  const showCaught = (award) => {
    if (!award || !award.pog_id) return;
    setCaughtPog(award);
    if (award.rarity === "legendary" || award.rarity === "epic") pogConfetti();
    if (caughtTimer.current) clearTimeout(caughtTimer.current);
    caughtTimer.current = setTimeout(() => setCaughtPog(null), 5500);
  };
  useEffect(() => () => { if (caughtTimer.current) clearTimeout(caughtTimer.current); }, []);

  // clean up the drive timer on unmount
  useEffect(() => () => { if (driveRef.current.timer) clearTimeout(driveRef.current.timer); }, []);

  const activeCatLabel = category ? categories.find((c) => c.id === category)?.label : "All / Random";
  const featuredBiz = businesses.find((b) => b.featured);
  const dealCount = businesses.filter((b) => b.has_deals || b.featured).length;
  const shownBusinesses = (dealsOnly && dealCount > 0) ? businesses.filter((b) => b.has_deals || b.featured) : businesses;

  useEffect(() => {
    if (dealCount > prevDealCountRef.current && prevDealCountRef.current !== 0) {
      setDealPulse(true);
      const t = setTimeout(() => setDealPulse(false), 1500);
      prevDealCountRef.current = dealCount;
      return () => clearTimeout(t);
    }
    prevDealCountRef.current = dealCount;
  }, [dealCount]);

  // 🔥 Trending near you — top categories among nearby businesses, deal-boosted.
  const trending = useMemo(() => {
    if (category || !businesses.length) return [];
    const m = {};
    for (const b of businesses) {
      if (!b.category_id || b.category_id === "other" || b.is_sale) continue;
      const e = m[b.category_id] || (m[b.category_id] = { id: b.category_id, label: b.category_label, icon: b.icon, count: 0, deals: 0 });
      e.count += 1;
      if (b.has_deals || b.featured) e.deals += 1;
    }
    return Object.values(m).sort((a, b) => (b.deals * 3 + b.count) - (a.deals * 3 + a.count)).slice(0, 6);
  }, [businesses, category]);
  // 🧩 Hobby & Collectibles spotlight — the most on-brand POG category gets pinned front & glowing.
  const trendingRail = useMemo(() => {
    const arr = [...trending];
    const hob = arr.find((t) => t.id === "hobby_collectibles");
    if (hob) {
      hob.spotlight = true;
      arr.splice(arr.indexOf(hob), 1);
      arr.unshift(hob);
    }
    return arr;
  }, [trending]);
  useEffect(() => {
    axios.get(`${API}/wanted`).then((r) => setWantedCount((r.data.posts || []).length)).catch(() => {});
  }, []);
  const showTrending = !truckerMode && !exploreActive && !nav && !ads && !category && trending.length > 0;

  // Red-dot nudge: is today's free Daily POG Drop still unclaimed?
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (driver) {
      axios.get(`${API}/consumer/points`, { withCredentials: true })
        .then((r) => setDailyUnclaimed(!r.data?.daily?.claimed_today))
        .catch(() => setDailyUnclaimed(false));
    } else {
      let d = "";
      try { d = localStorage.getItem("airpog_daily_date") || ""; } catch { /* ignore */ }
      setDailyUnclaimed(d !== today);
    }
  }, [driver, moreOpen]);

  // 🧩🎩 More-menu widgets — today's puzzle status + walk rolls/vault.
  const [wordStatus, setWordStatus] = useState(null);
  const [walkStatus, setWalkStatus] = useState(null);
  useEffect(() => {
    if (!driver || !moreOpen) return;
    axios.get(`${API}/pogword/state`, { withCredentials: true })
      .then((r) => setWordStatus({ solved: r.data.levels.filter((l) => l.solved).length, streak: r.data.streak || 1 }))
      .catch(() => {});
    axios.get(`${API}/pogwalk/widget`, { withCredentials: true })
      .then((r) => setWalkStatus({ remaining: r.data.remaining, vault: r.data.vault, coupons: r.data.coupons || 0, expiring: r.data.expiring || 0 }))
      .catch(() => {});
  }, [driver, moreOpen]);

  // One-time install reward: claim on standalone launch (covers install-before-login) + celebrate.
  const [installRewardToast, setInstallRewardToast] = useState(null);
  useEffect(() => {
    if (driver && appInstalled()) claimInstallReward();
  }, [driver]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onReward = (e) => {
      setInstallRewardToast(e.detail);
      try { pogConfetti(); } catch { /* ignore */ }
      setTimeout(() => setInstallRewardToast(null), 8000);
    };
    window.addEventListener("airpog:install-reward", onReward);
    return () => window.removeEventListener("airpog:install-reward", onReward);
  }, []);

  return (
    <div className={`relative h-screen w-screen overflow-hidden bg-[#0F172A] text-white ${(typeof window !== "undefined" && (window.location.search.includes("night") || new Date().getHours() >= 20 || new Date().getHours() < 6)) ? "airpog-night" : ""}`}>
      <div ref={mapEl} data-testid={GPS.map} className="absolute inset-0 z-0" />
      <span data-testid="user-pos" className="sr-only">{userPos ? `${userPos.lat},${userPos.lng}` : ""}</span>

      {/* Top controls — compact: a single Filters pill (left) + primary actions & overflow (right) */}
      <MapTopBar
        onSearch={() => setOmniOpen(true)}
        onOpenFilters={() => setFiltersOpen(true)}
        activeCatLabel={activeCatLabel}
        travelMode={travelMode}
        radius={radius}
        onAsk={() => setAskOpen(true)}
        onCollect={() => navigate("/collect")}
        onPogodex={() => setPogodexOpen(true)}
        onBoard={() => { setBoardOpen(true); setBoardUnread(false); }}
        boardUnread={boardUnread}
        driver={driver}
        onPlus={() => (driver ? navigate("/hq") : setPlusOpen(true))}
        onJoin={() => loginGoogle?.()}
        onRecenter={recenter}
      >
        <MapMoreMenu
          open={moreOpen}
          onToggle={() => setMoreOpen((v) => !v)}
          onClose={() => setMoreOpen(false)}
          dailyUnclaimed={dailyUnclaimed}
          dmUnread={dmUnread}
          wordStatus={wordStatus}
          walkStatus={walkStatus}
          driving={driving}
          toggleDrive={toggleDrive}
          layout={layout}
          setLayout={setLayout}
          mapTheme={mapTheme}
          toggleMapTheme={toggleMapTheme}
          calm={calm}
          toggleCalm={toggleCalm}
          setLocOpen={setLocOpen}
          openAddBiz={openAddBiz}
          openPostPog={openPostPog}
          unleashPogzilla={unleashPogzilla}
          launchBalloons={launchBalloons}
          appInstalled={appInstalled}
          promptInstall={promptInstall}
        />
      </MapTopBar>

      {/* Trucker quick-filter chips — appear when Trucker Mode is on */}
      <AnimatePresence>
        {truckerMode && (
          <motion.div
            data-testid="trucker-chip-row"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute top-[6.5rem] sm:top-24 inset-x-0 z-[1150] px-3 sm:px-5 flex gap-2 overflow-x-auto no-scrollbar pointer-events-auto"
          >
            <button
              data-testid="trucker-chip-all"
              onClick={() => setCategory(null)}
              className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${!category ? "bg-amber-400 text-slate-900" : "glass text-amber-200 hover:bg-slate-700/60"}`}
            >
              All / Random
            </button>
            {categories.filter((c) => c.trucker).map((c) => {
              const Ico = getIcon(c.icon);
              const on = category === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`trucker-chip-${c.id}`}
                  onClick={() => setCategory(c.id)}
                  className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 transition-colors ${on ? "bg-amber-400 text-slate-900" : "glass text-amber-200 hover:bg-slate-700/60"}`}
                >
                  <Ico className="w-3.5 h-3.5" /> {t(`categories.${c.id}`, { defaultValue: c.label })}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 📍 Deals Near You pill — riding the very top, center stage (per Sir Eric) */}
      <div className="absolute top-[calc(max(0.75rem,env(safe-area-inset-top))+3.4rem)] inset-x-0 z-[1200] px-3 flex flex-wrap items-start justify-center gap-2 pointer-events-none">
        <AnimatePresence>
          {businesses.length > 0 && !nav && !ads && !exploreActive && !featuredBiz && !truckerMode && (
            <motion.div
              data-testid="live-spots-counter"
              initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0, scale: dealPulse ? [1, 1.08, 1] : 1 }} exit={{ opacity: 0, y: -14 }}
              transition={{ scale: { duration: 0.6 } }}
              className="shrink-0 pointer-events-auto flex items-center gap-1.5"
            >
              <div className={`flex items-center gap-2 whitespace-nowrap rounded-full pl-3 pr-2 py-1.5 shadow-lg bg-gradient-to-r from-[#582FFF] to-[#8A00FF] text-white transition-shadow duration-500 ${dealPulse ? "ring-2 ring-amber-300 shadow-amber-400/60" : "shadow-indigo-500/30"} ${dealsOnly ? "ring-2 ring-amber-300" : ""}`}>
              <MapPin className="w-4 h-4 shrink-0 text-white" />
              <span className="text-xs sm:text-sm font-heading font-bold pr-1 text-white">
                {t("map.dealsNearYou")} <span className="text-white/70 font-semibold">· {businesses.length}</span>
              </span>
              {poggedZone && (
                <span data-testid="pogged-zone-badge" title="FULLY POG'D ZONE — instant local data, every business loaded" className={`flex items-center rounded-full px-2 py-0.5 text-[10px] font-heading font-black tracking-wide whitespace-nowrap transition-colors ${zoneWelcome ? "bg-emerald-400 text-slate-900 animate-pulse" : "bg-emerald-400/25 text-emerald-200"}`}>
                  {zoneWelcome ? t("map.pogdZoneWelcome") : t("map.pogdZone")}
                </span>
              )}
              {!poggedZone && zoneLoading !== null && (
                <span data-testid="territory-loading-badge" title="We're loading every business in this territory right now — deals incoming!" className="flex items-center rounded-full px-2 py-0.5 text-[10px] font-heading font-black tracking-wide whitespace-nowrap bg-sky-400/25 text-sky-200 animate-pulse">
                  🚚 {zoneLoading ? `${zoneLoading.toUpperCase()} LOADING…` : "NEW TERRITORY LOADING…"}
                </span>
              )}
              {dealCount > 0 && (
                <motion.button
                  data-testid="deals-filter-toggle"
                  onClick={() => setDealsOnly((v) => !v)}
                  animate={dealPulse ? { scale: [1, 1.2, 1] } : { scale: 1 }}
                  transition={{ duration: 0.55 }}
                  className={`flex items-center gap-1 rounded-full pl-2 pr-2.5 py-1 text-xs font-heading font-bold transition-colors ${dealsOnly ? "bg-amber-400 text-slate-900" : "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25"}`}
                  title={dealsOnly ? "Show all spots" : "Show only live deals"}
                >
                  <Flame className={`w-3.5 h-3.5 ${dealPulse && !dealsOnly ? "animate-pulse" : ""}`} />
                  {dealsOnly ? <X className="w-3 h-3 ml-0.5" /> : null}
                  {dealCount} live {dealCount === 1 ? "deal" : "deals"}
                </motion.button>
              )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!nav && !ads && !exploreActive && !truckerMode && <EventCountdownPill />}
      </div>

      {/* 🪪 POG'RS IDENTITY BADGE — your minted POG, floating on the map (mockup: top-right) */}
      {!nav && !ads && !placingBiz && (
        <div className="absolute top-[6.1rem] sm:top-[6.6rem] right-3 z-[1139] pointer-events-auto">
          <IdentityBadge
            uid={driver?.user_id}
            name={driver?.pog_nickname || driver?.name || "P"}
            size={66}
            ver={badgeVer || undefined}
            showFlair
            onClick={() => (driver ? setBadgeStudioOpen(true) : loginGoogle?.())}
            testid="map-identity-badge"
          />
        </div>
      )}

      {/* Explore / destination banner — scanning a spot that isn't your GPS */}
      <AnimatePresence>
        {exploreActive && !nav && !ads && (
          <motion.div
            data-testid="explore-banner"
            initial={{ opacity: 0, y: -16, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -16, x: "-50%" }}
            className={`absolute left-1/2 -translate-x-1/2 z-[1160] flex items-center gap-2.5 rounded-full pl-3 pr-2 py-1.5 max-w-[92vw] glass-strong border border-sky-400/40 shadow-xl ${truckerMode ? "top-[9rem] sm:top-36" : "top-16 sm:top-20"}`}
          >
            {(() => { const MIco = TRAVEL_MODES[travelMode].icon; return <MIco className="w-4 h-4 text-sky-300 shrink-0" />; })()}
            <span className="text-xs sm:text-sm font-heading font-semibold truncate max-w-[55vw]">
              {travelMode === "fly" ? "Deals waiting in " : travelMode === "transit" ? "Along the way to " : "Exploring: "}{exploreLabel}
            </span>
            {poggedZone && (
              <span data-testid="scan-pogd-zone-flash" className="shrink-0 flex items-center rounded-full px-2 py-0.5 text-[10px] font-heading font-black tracking-wide bg-emerald-400 text-slate-900 animate-pulse whitespace-nowrap" title="FULLY POG'D ZONE — instant local data">
                ⚡ POG'D ZONE
              </span>
            )}
            <button
              data-testid="explore-back-btn"
              onClick={recenter}
              className="shrink-0 h-7 px-3 rounded-full bg-sky-500 text-slate-900 text-xs font-bold hover:bg-sky-400 transition-colors flex items-center gap-1"
            >
              <Crosshair className="w-3.5 h-3.5" /> Back to me
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🕐 Recent POG SCANS — one-tap jump back to spots you scanned before */}
      <AnimatePresence>
        {exploreActive && !nav && !ads && scanHistory.filter((s) => s.label !== exploreLabel).length > 0 && (
          <motion.div
            data-testid="scan-history-row"
            initial={{ opacity: 0, y: -8, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -8, x: "-50%" }}
            className={`absolute left-1/2 -translate-x-1/2 z-[1155] flex items-center gap-1.5 max-w-[92vw] overflow-x-auto no-scrollbar ${truckerMode ? "top-[11.5rem] sm:top-[11.75rem]" : "top-[6.75rem] sm:top-[7.75rem]"}`}
          >
            <span className="shrink-0 text-[9px] font-black tracking-widest uppercase text-slate-400">🌀 Recent warps</span>
            {scanHistory.filter((s) => s.label !== exploreLabel).slice(0, 3).map((s) => (
              <button
                key={s.label}
                data-testid={`scan-history-chip-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setExplore(s.lat, s.lng, s.label)}
                className="shrink-0 h-7 px-2.5 rounded-full glass border border-cyan-400/30 text-[11px] font-bold text-cyan-200 hover:bg-slate-800/80 transition-colors whitespace-nowrap"
              >
                🎯 {s.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Driving status pill */}
      <AnimatePresence>
        {driving && (
          <motion.div
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="absolute top-16 right-3 sm:top-20 sm:right-5 z-[1300] glass rounded-full px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-2 text-xs sm:text-sm font-heading font-semibold text-emerald-300"
          >
            <Radio className="w-4 h-4 animate-pulse shrink-0" /> <span className="hidden sm:inline">Driving — businesses update live</span><span className="sm:hidden">Driving…</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deal of the Day banner (featured business nearby) */}
      <AnimatePresence>
        {featuredBiz && !nav && !ads && !driver?.is_plus && (
          <motion.button
            data-testid="deal-of-the-day-banner"
            initial={{ opacity: 0, y: -16, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -16, x: "-50%" }}
            onClick={() => showAds(featuredBiz)}
            className="absolute top-16 left-1/2 -translate-x-1/2 sm:top-20 z-[1150] flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2 max-w-[92vw] bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow-xl shadow-amber-500/30 hover:scale-[1.02] transition-transform"
          >
            <span className="flex items-center gap-1 bg-slate-900/15 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider shrink-0">
              <Flame className="w-3.5 h-3.5" /> Deal of the Day
            </span>
            <span className="font-heading font-bold text-sm truncate">{featuredBiz.name}{featuredBiz.featured_deal ? ` · ${featuredBiz.featured_deal}` : ""}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Location accuracy guard — browser returned an imprecise (IP-based) fix */}
      <AnimatePresence>
        {geoUncertain && !geoDismissed && !nav && !ads && !exploreActive && (
          <motion.div
            data-testid="geo-uncertain-banner"
            initial={{ opacity: 0, y: -14, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -14, x: "-50%" }}
            className={`absolute ${showTrending ? "top-[9.5rem] sm:top-[10rem]" : "top-[6.5rem] sm:top-[7.5rem]"} left-1/2 -translate-x-1/2 z-[1160] max-w-[94vw] flex items-center gap-2 rounded-2xl pl-3 pr-2 py-2 glass-strong border border-amber-400/40 shadow-xl`}
          >
            <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-xs sm:text-sm text-slate-200 leading-snug">
              {detectedCity ? <>Looks like you're near <span className="font-semibold text-white">{detectedCity}</span> — not right?</> : <>We couldn't pin your exact spot.</>}
              <button
                data-testid="geo-set-location-btn"
                onClick={() => { setLocOpen(true); }}
                className="ml-1 font-bold text-amber-300 underline underline-offset-2"
              >
                Set your city
              </button>
            </span>
            <button
              data-testid="geo-uncertain-dismiss"
              onClick={() => setGeoDismissed(true)}
              className="shrink-0 w-7 h-7 rounded-lg hover:bg-slate-700/60 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Live "spots near you" counter moved next to the POG STRIP pill (top row above) */}


      {/* Top-center: navigation panel (sits below controls on mobile) */}
      <NavPanel
        nav={nav}
        navigateExternal={navigateExternal}
        onChooseGps={setGpsSheet}
        clearRoute={clearRoute}
      />

      {/* Routing spinner */}
      {routing && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1200] glass-strong rounded-2xl px-6 py-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-sky-400 animate-spin" />
          <span className="font-heading font-semibold">Calculating route…</span>
        </div>
      )}

      {/* Error toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute bottom-44 left-1/2 -translate-x-1/2 z-[1100] glass-strong rounded-xl px-4 py-2.5 text-sm text-rose-200 border border-rose-500/30"
            onClick={() => setError(null)}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🚂 THE POG EXPRESS — random-route merchant train for extra exposure */}
      {!calm && <PogExpress businesses={businesses} onCarTap={handleTabClick} center={userPos} radius={radius} />}
      <ChampSalute />
      {/* 🏃⚡ INSTANT POG RUN — courier dispatch sheet (opens via window event) */}
      <InstantRunSheet userPos={userPos} />
      {/* 🔎♥ QUICK FIND — saved merchants sheet (opens via ♥ top-bar button) */}
      <QuickFindSheet />

      {/* Business strip */}
      {!placingBiz && <BusinessStrip
        stripRef={stripRefFn}
        layout={layout}
        loading={loading}
        warming={warming}
        poggedZone={poggedZone}
        radius={radius}
        exploreActive={exploreActive}
        exploreLabel={exploreLabel}
        businesses={businesses}
        shownBusinesses={shownBusinesses}
        activeId={activeId}
        onTabClick={handleTabClick}
        driver={driver}
        onFav={onFav}
        isFav={isFav}
        favBusy={favBusy}
        voteBiz={voteBiz}
        hasVoted={hasVoted}
        navigate={navigate}
        spotlightIds={spotlightIds}
      />}

      {/* Expanded POG AD — 🎯 route / 🔗 POG Link / Ⓐ app / 🌐 web + REDEEM */}
      <PogAdPanel
        pogAd={pogAd}
        onClose={() => setPogAd(null)}
        onRoute={(b) => { setPogAd(null); routeTo(b); }}
        onRedeem={(deal, b) => { setPogAd(null); startRedeem(deal, b.id); }}
        openLink={openLink}
      />

      {/* Filters bottom sheet — consolidates travel mode, radius, trucker & categories */}
      <FiltersSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        travelMode={travelMode}
        changeMode={changeMode}
        radius={radius}
        setRadius={setRadius}
        truckerMode={truckerMode}
        toggleTrucker={toggleTrucker}
        category={category}
        setCategory={setCategory}
        categories={categories}
      />

      {/* 🌆 TWILIGHT sky wash — blues & purples with that pink */}
      {mapTheme === "twilight" && (
        <div className="pointer-events-none absolute inset-0 z-[500] bg-gradient-to-b from-indigo-700/30 via-purple-600/15 to-fuchsia-500/25 mix-blend-overlay" data-testid="twilight-wash" />
      )}

      {/* 🌙⚡ POG NEON NIGHT — electric city glow: cyan/fuchsia auras + vignette + glowing pins */}
      {mapTheme === "night" && (
        <>
          <div
            className="pointer-events-none absolute inset-0 z-[500]"
            data-testid="neon-night-wash"
            style={{
              background:
                "radial-gradient(ellipse 60% 45% at 50% 25%, rgba(34,211,238,0.08), transparent 60%)," +
                "radial-gradient(ellipse 50% 40% at 15% 85%, rgba(232,121,249,0.11), transparent 55%)," +
                "radial-gradient(ellipse 50% 40% at 88% 78%, rgba(52,211,153,0.08), transparent 55%)",
              boxShadow: "inset 0 0 160px 60px rgba(2,6,23,0.55)",
            }}
          />
          <style>{`
            .airpin{filter:drop-shadow(0 0 7px rgba(34,211,238,.6)) drop-shadow(0 3px 4px rgba(15,23,42,.5)) !important}
            .airpin.active{filter:drop-shadow(0 0 11px rgba(232,121,249,.85)) drop-shadow(0 3px 4px rgba(15,23,42,.5)) !important}
            @keyframes pogNightlife{0%,100%{filter:drop-shadow(0 0 8px rgba(251,191,36,.75)) drop-shadow(0 3px 4px rgba(15,23,42,.5))}50%{filter:drop-shadow(0 0 16px rgba(251,146,60,.95)) drop-shadow(0 3px 4px rgba(15,23,42,.5))}}
            .airpin.airpin-nl{animation:pogNightlife 1.8s ease-in-out infinite !important}
            @keyframes cyberBreathe{0%,100%{box-shadow:0 0 16px var(--cybg),0 0 34px var(--cybg2),inset 0 0 14px rgba(255,255,255,.05)}50%{box-shadow:0 0 26px var(--cybg),0 0 54px var(--cybg2),inset 0 0 20px rgba(255,255,255,.08)}}
            .cyber-card{animation:cyberBreathe 3.2s ease-in-out infinite}
          `}</style>
        </>
      )}

      {/* 🖤 BLACKOUT — pitch-black void, only the features live: every pin becomes a star */}
      {mapTheme === "blackout" && (
        <style>{`
          .airpin{filter:drop-shadow(0 0 8px rgba(34,211,238,.8)) drop-shadow(0 0 3px rgba(255,255,255,.35)) !important}
          .airpin.active{filter:drop-shadow(0 0 14px rgba(232,121,249,.95)) drop-shadow(0 0 4px rgba(255,255,255,.4)) !important}
          @keyframes pogNightlife{0%,100%{filter:drop-shadow(0 0 9px rgba(251,191,36,.85))}50%{filter:drop-shadow(0 0 18px rgba(251,146,60,1))}}
          .airpin.airpin-nl{animation:pogNightlife 1.8s ease-in-out infinite !important}
          @keyframes cyberBreathe{0%,100%{box-shadow:0 0 18px var(--cybg),0 0 40px var(--cybg2),inset 0 0 14px rgba(255,255,255,.05)}50%{box-shadow:0 0 30px var(--cybg),0 0 62px var(--cybg2),inset 0 0 22px rgba(255,255,255,.09)}}
          .cyber-card{animation:cyberBreathe 3.2s ease-in-out infinite}
        `}</style>
      )}

      {/* 🔍 OMNIBOX — intent-driven search + category grid (Phase 2 remodel) */}
      <Omnibox
        open={omniOpen}
        onClose={() => setOmniOpen(false)}
        categories={categories}
        activeCategory={category}
        onCategory={setCategory}
        businesses={businesses}
        spotlightIds={spotlightIds}
        onPickBusiness={(b) => handleTabClick(b)}
      />

      {/* 🔥 POG STRIP trending — bottom middle, riding the business strip's lip */}
      {!nav && !ads && !placingBiz && (
        <div className={`absolute ${layout === "double" ? "bottom-[calc(26rem+env(safe-area-inset-bottom))]" : "bottom-[calc(17.25rem+env(safe-area-inset-bottom))]"} inset-x-0 z-[1010] flex justify-center pointer-events-none px-3`}>
          <div className="pointer-events-auto max-w-full overflow-x-auto">
            <PogStrip
              show={showTrending}
              rail={trendingRail}
              wantedCount={wantedCount}
              onCategory={setCategory}
              onWanted={() => navigate("/wanted")}
              jobsCount={jobsCount}
              jobsNew={jobsNew}
              onJobs={() => navigate("/jobs")}
              reelsCount={reelsCount}
              reelsNow={reelsNow}
              onReels={() => navigate("/flics")}
              saleWeekend={[0, 5, 6].includes(new Date().getDay()) ? saleNow : 0}
              onSaleWeekend={() => navigate("/reels?sales=1")}
            />
          </div>
        </div>
      )}

      {/* 🏰 POG KINGDOM (pull down) + 📡 POG BEAM (pull up) edge gestures */}
      <KingdomTab onOpen={() => setKingdomOpen(true)} />
      <KingdomSheet open={kingdomOpen} onClose={() => setKingdomOpen(false)} businesses={businesses} onMerchantTap={handleTabClick} />
      {driver && (
        <BadgeStudio open={badgeStudioOpen} onClose={() => setBadgeStudioOpen(false)}
          uid={driver.user_id} name={driver.pog_nickname || driver.name} ghost={!!driver.leaderboard_hidden} onSaved={refresh} />
      )}
      <RankUpSplash rank={rankUp} onClose={() => setRankUp(null)} />

      {/* 📖 POG'ODEX — every radar merchant indexed by category */}
      <PogodexSheet
        open={pogodexOpen}
        onClose={() => setPogodexOpen(false)}
        businesses={businesses}
        categories={categories}
        driver={driver}
        hot={hot}
        onMerchant={(b) => { setPogodexOpen(false); handleTabClick(b); }}
        onRoute={(b) => { setPogodexOpen(false); routeTo(b); }}
        onFilterCategory={(id) => { setPogodexOpen(false); setCategory(id); }}
        activeCategory={category}
        openLink={openLink}
      />

      {/* 📜 THE POG SCROLL — HQ notifications board */}
      <PogBoardSheet open={boardOpen} onClose={() => setBoardOpen(false)} driver={driver} pos={userPos} />

      {/* 🎯 POG SCAN reticle / radar sweep */}
      <PogScanReticle fx={scanFx} />
      <SpotlightFlyer mapRef={mapRef} onOpen={(b, spot) => {
        if (spot?.business_id) setSpotlightIds((m) => { const n = new Map(m); n.set(spot.business_id, spot); return n; });
        showAds(b);
      }} />

      {/* 📖 One-time POG'ODEX coach bubble */}
      {odexTip && (
        <div className="absolute top-[4.2rem] left-3 sm:left-36 z-[1350] pointer-events-auto" data-testid="odex-coach-tip">
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="relative glass-strong border border-amber-400/60 rounded-2xl px-3.5 py-2.5 shadow-xl max-w-[240px]">
            <div className="absolute -top-1.5 left-6 w-3 h-3 rotate-45 bg-slate-900 border-l border-t border-amber-400/60" />
            <p className="text-[11px] font-heading font-extrabold text-amber-200">📖 NEW! POG'ODEX</p>
            <p className="text-[11px] text-slate-300 font-semibold mt-0.5">Browse every merchant near you by category — deals, routes & more!</p>
            <div className="flex gap-2 mt-2">
              <button data-testid="odex-tip-open" onClick={() => { dismissOdexTip(); setPogodexOpen(true); }} className="text-[10px] font-black text-slate-900 bg-amber-400 rounded-full px-2.5 py-1">OPEN IT</button>
              <button data-testid="odex-tip-dismiss" onClick={dismissOdexTip} className="text-[10px] font-bold text-slate-400 px-1">Got it</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ⭐ POGMARK deal alert — a bookmarked merchant has a fresh deal */}
      {markAlert && (
        <button
          data-testid="pogmark-deal-alert"
          onClick={() => { const b = markAlert; setMarkAlert(null); handleTabClick(b); }}
          className="absolute left-1/2 -translate-x-1/2 bottom-[19rem] z-[1750] glass-strong border border-amber-400/60 rounded-full px-4 py-2 text-xs font-heading font-extrabold text-amber-200 shadow-xl whitespace-nowrap pointer-events-auto"
        >
          ⭐ {markAlert.name} has a live deal! Tap to see it 🔥
        </button>
      )}

      {/* 🌟 REEL STAR trailer — relocated to the FLICS page (map decluttering) */}

      {/* Add a missing spot — step 1: place the pin (crosshair over pannable map) */}
      {placingBiz && (
        <>
          <div className="absolute inset-0 z-[2040] pointer-events-none flex items-center justify-center">
            <motion.div initial={{ y: -6 }} animate={{ y: 0 }} className="-translate-y-4">
              <MapPin className="w-11 h-11 text-emerald-500 drop-shadow-[0_4px_6px_rgba(0,0,0,0.4)]" strokeWidth={2.5} fill="rgba(16,185,129,0.25)" />
            </motion.div>
          </div>
          <div data-testid="place-biz-bar" className="absolute left-0 right-0 bottom-0 z-[2046] p-4 pb-[calc(3.75rem+env(safe-area-inset-bottom))] sm:pb-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col items-center gap-2.5">
            <div className="glass-strong rounded-full px-4 py-2 text-sm font-bold text-white shadow-lg flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-300" /> Pan the map so the pin sits on the spot
            </div>
            <div className="flex gap-2 w-full max-w-md">
              <button data-testid="place-biz-cancel" onClick={() => setPlacingBiz(false)} className="flex-1 h-12 rounded-xl bg-slate-800/85 text-slate-200 font-heading font-bold">Cancel</button>
              <button data-testid="place-biz-confirm" onClick={confirmPlaceBiz} className="flex-1 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-sky-500 text-white font-heading font-bold flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Pin here</button>
            </div>
          </div>
        </>
      )}

      {/* Add a missing spot — step 2: details */}
      <AnimatePresence>
        {addBizOpen && addBizForm && (
          <motion.div
            className="absolute inset-0 z-[2050] flex items-end sm:items-center justify-center bg-black/55"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setAddBizOpen(false)}
          >
            <motion.div
              data-testid="add-business-modal"
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] relative max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between mb-3 shrink-0">
                <h2 className="font-heading text-lg font-bold flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-400" /> Add a missing spot</h2>
                <button data-testid="add-business-close" onClick={() => setAddBizOpen(false)} className="w-9 h-9 rounded-xl hover:bg-slate-700/60 flex items-center justify-center"><X className="w-4 h-4 text-slate-300" /></button>
              </div>
              {addBizDone ? (
                <div data-testid="add-business-success" className="overflow-y-auto no-scrollbar">
                  <div className="rounded-2xl bg-emerald-500/15 border border-emerald-400/30 p-4 mb-4 text-center">
                    <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="font-heading font-bold">Pinned it! 📍 POGNIFICENT!</p>
                    <p className="text-sm text-slate-300 mt-1">
                      {addBizDone.points_earned > 0
                        ? `+${addBizDone.points_earned} POG BUCKS for helping the map grow. It's live for nearby POG'RS now.`
                        : "Thanks for growing the map! (You've hit today's reward cap — keep 'em coming tomorrow.)"}
                    </p>
                  </div>
                  <button onClick={() => setAddBizOpen(false)} className="w-full h-11 rounded-xl bg-slate-800/70 hover:bg-slate-700/70 font-heading font-bold text-sm">Done</button>
                </div>
              ) : (
                <div className="overflow-y-auto no-scrollbar -mx-1 px-1">
                  <div className="rounded-xl bg-sky-500/10 border border-sky-400/25 p-3 mb-4 text-xs text-sky-200 flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Pin dropped at <b>{addBizForm.lat.toFixed(4)}, {addBizForm.lng.toFixed(4)}</b>. Tell us what's there:</span>
                  </div>
                  <label className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">Business name</label>
                  <input data-testid="add-business-name" value={addBizForm.name} onChange={(e) => setAddBizForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Rio Grande Dispensary" className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-emerald-400/60 mt-1.5 mb-3" />
                  <label className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">Category</label>
                  <select data-testid="add-business-category" value={addBizForm.category_id} onChange={(e) => setAddBizForm((f) => ({ ...f, category_id: e.target.value }))} className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-emerald-400/60 mt-1.5 mb-3">
                    <option value="">Pick a category…</option>
                    {categories.filter((c) => !c.trucker).map((c) => (<option key={c.id} value={c.id}>{t(`categories.${c.id}`, { defaultValue: c.label })}</option>))}
                  </select>
                  <label className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">Address <span className="text-slate-500 normal-case font-normal">(optional)</span></label>
                  <input data-testid="add-business-address" value={addBizForm.address} onChange={(e) => setAddBizForm((f) => ({ ...f, address: e.target.value }))} placeholder="Street, city" className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-emerald-400/60 mt-1.5 mb-3" />
                  {addBizForm.category_id === "yard_sale" && (
                    <>
                      <label className="text-[11px] font-bold tracking-[0.18em] uppercase text-amber-300">🏷 Last day of the sale</label>
                      <input data-testid="add-business-ends-on" type="date" value={addBizForm.ends_on || ""} onChange={(e) => setAddBizForm((f) => ({ ...f, ends_on: e.target.value }))} className="w-full h-11 rounded-xl bg-slate-800/70 border border-amber-500/40 px-3 text-sm outline-none focus:border-amber-400/70 mt-1.5 mb-1" />
                      <p className="text-[10px] text-slate-500 mb-3">The pin disappears automatically after this date.</p>
                    </>
                  )}
                  {addBizForm.err && <p data-testid="add-business-error" className="text-rose-400 text-xs mb-2">{addBizForm.err}</p>}
                  <button data-testid="add-business-submit" disabled={addBizBusy} onClick={submitAddBiz} className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-sky-500 text-white font-heading font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                    {addBizBusy ? "Pinning…" : <><MapPin className="w-4 h-4" /> Drop the pin (+5 POG BUCKS)</>}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Post a POG — consumer self-serve garage sale / party / event / shout-out */}
      <AnimatePresence>
        {pogOpen && pogForm && (
          <motion.div
            className="absolute inset-0 z-[2050] flex items-end sm:items-center justify-center bg-black/55"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setPogOpen(false); }}
          >
            <motion.div
              data-testid="post-pog-modal"
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] relative max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between mb-3 shrink-0">
                <h2 className="font-heading text-lg font-bold flex items-center gap-2"><PartyPopper className="w-5 h-5 text-fuchsia-400" /> Post a POG</h2>
                <button data-testid="post-pog-close" onClick={() => setPogOpen(false)} className="w-9 h-9 rounded-xl hover:bg-slate-700/60 flex items-center justify-center"><X className="w-4 h-4 text-slate-300" /></button>
              </div>

              {pogShare ? (
                <div data-testid="pog-success" className="overflow-y-auto no-scrollbar">
                  <div className="rounded-2xl bg-emerald-500/15 border border-emerald-400/30 p-4 mb-4 text-center">
                    <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="font-heading font-bold">Your POG is live! 🎉</p>
                    <p className="text-sm text-slate-300 mt-1">
                      {pogShare.visibility === "private"
                        ? "It's private — share the link with whoever you choose."
                        : "It's on the map for nearby people to find."}
                    </p>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <input readOnly value={`${window.location.origin}/api/pog/${pogShare.id}`} className="flex-1 h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-xs outline-none" />
                    <button data-testid="pog-share-btn" onClick={() => sharePog(pogShare.id)} className="h-11 px-4 rounded-xl bg-fuchsia-500 text-white font-bold text-sm flex items-center gap-1.5 hover:bg-fuchsia-400">
                      {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Share2 className="w-4 h-4" /> Share</>}
                    </button>
                  </div>
                  <button onClick={() => setPogOpen(false)} className="w-full h-11 rounded-xl bg-slate-800/70 hover:bg-slate-700/70 font-heading font-bold text-sm">Done</button>
                </div>
              ) : (
                <div className="overflow-y-auto no-scrollbar -mx-1 px-1">
                  {/* Type */}
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2">What kind of POG?</p>
                  <p data-testid="little-giants-line" className="text-[11px] font-bold text-amber-300 mb-2">🍋 Lemonade stands, bake sales &amp; kid-run stands post FREE — the map belongs to the LITTLE GIANTS too! ⚡</p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {POG_PRESETS.map((t) => (
                      <button
                        key={t}
                        data-testid={`pog-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => setPogForm((f) => ({ ...f, type: t }))}
                        className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${pogForm.type === t ? "bg-fuchsia-500 text-white" : "bg-slate-800/60 text-slate-300 hover:bg-slate-700/60"}`}
                      >
                        {t}
                      </button>
                    ))}
                    <button
                      data-testid="pog-type-custom"
                      onClick={() => setPogForm((f) => ({ ...f, type: "__custom__" }))}
                      className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${pogForm.type === "__custom__" ? "bg-fuchsia-500 text-white" : "bg-slate-800/60 text-slate-300 hover:bg-slate-700/60"}`}
                    >
                      + Custom
                    </button>
                  </div>
                  {pogForm.type === "__custom__" && (
                    <input
                      data-testid="pog-custom-input"
                      value={pogForm.custom}
                      onChange={(e) => setPogForm((f) => ({ ...f, custom: e.target.value }))}
                      placeholder="Name your POG (e.g. Birthday POG)"
                      className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-fuchsia-400/60 mb-3"
                    />
                  )}

                  {/* Visibility */}
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2 mt-2">Who can see it?</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      data-testid="pog-visibility-public"
                      onClick={() => setPogForm((f) => ({ ...f, visibility: "public" }))}
                      className={`h-14 rounded-xl flex items-center gap-2 px-3 text-left transition-colors ${pogForm.visibility === "public" ? "bg-sky-500/20 border border-sky-400/40" : "bg-slate-800/60 hover:bg-slate-700/60"}`}
                    >
                      <Globe className="w-5 h-5 text-sky-400 shrink-0" />
                      <span><span className="block text-sm font-bold">Public</span><span className="block text-[10px] text-slate-400">On the map</span></span>
                    </button>
                    <button
                      data-testid="pog-visibility-private"
                      onClick={() => setPogForm((f) => ({ ...f, visibility: "private" }))}
                      className={`h-14 rounded-xl flex items-center gap-2 px-3 text-left transition-colors ${pogForm.visibility === "private" ? "bg-fuchsia-500/20 border border-fuchsia-400/40" : "bg-slate-800/60 hover:bg-slate-700/60"}`}
                    >
                      <Lock className="w-5 h-5 text-fuchsia-400 shrink-0" />
                      <span><span className="block text-sm font-bold">Private</span><span className="block text-[10px] text-slate-400">Link only</span></span>
                    </button>
                  </div>

                  {/* Location mode — garage sale (share address) vs delivery only (hide it) */}
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2 mt-2">Location</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      data-testid="pog-loc-exact"
                      onClick={() => setPogForm((f) => ({ ...f, location_mode: "exact" }))}
                      className={`h-16 rounded-xl flex items-center gap-2 px-3 text-left transition-colors ${pogForm.location_mode === "exact" ? "bg-emerald-500/20 border border-emerald-400/40" : "bg-slate-800/60 hover:bg-slate-700/60"}`}
                    >
                      <MapPin className="w-5 h-5 text-emerald-400 shrink-0" />
                      <span><span className="block text-sm font-bold">At my spot</span><span className="block text-[10px] text-slate-400">Exact pin + address (garage sale)</span></span>
                    </button>
                    <button
                      data-testid="pog-loc-delivery"
                      onClick={() => setPogForm((f) => ({ ...f, location_mode: "delivery" }))}
                      className={`h-16 rounded-xl flex items-center gap-2 px-3 text-left transition-colors ${pogForm.location_mode === "delivery" ? "bg-amber-500/20 border border-amber-400/40" : "bg-slate-800/60 hover:bg-slate-700/60"}`}
                    >
                      <Truck className="w-5 h-5 text-amber-400 shrink-0" />
                      <span><span className="block text-sm font-bold">Delivery only</span><span className="block text-[10px] text-slate-400">Hide address · fuzz my pin</span></span>
                    </button>
                  </div>

                  {pogForm.location_mode === "delivery" && (
                    <div data-testid="pog-delivery-opts" className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 mb-4 space-y-3">
                      <p className="text-[11px] text-amber-200 flex items-start gap-1.5"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Your exact address stays private. Buyers see a fuzzed ~½-mile area and arrange delivery.</p>
                      <div>
                        <label className="text-[11px] font-bold text-slate-300 flex items-center justify-between">
                          <span>Deliver within</span>
                          <span className="text-amber-300 font-heading font-extrabold">{pogForm.delivery_radius} mi</span>
                        </label>
                        <input
                          data-testid="pog-delivery-radius"
                          type="range" min="1" max="100" value={pogForm.delivery_radius}
                          onChange={(e) => setPogForm((f) => ({ ...f, delivery_radius: Number(e.target.value) }))}
                          className="w-full accent-amber-400 mt-1"
                        />
                      </div>
                      <input
                        data-testid="pog-delivery-note"
                        value={pogForm.delivery_note}
                        onChange={(e) => setPogForm((f) => ({ ...f, delivery_note: e.target.value.slice(0, 120) }))}
                        placeholder="Delivery note (e.g. Free within 10 mi · Venmo/cash)"
                        className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-amber-400/60"
                      />
                      <input
                        data-testid="pog-contact"
                        value={pogForm.contact}
                        onChange={(e) => setPogForm((f) => ({ ...f, contact: e.target.value.slice(0, 120) }))}
                        placeholder="How buyers reach you — phone, email or link"
                        className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-amber-400/60"
                      />
                      <p className="text-[10px] text-slate-400">Buyers get a "Request delivery" button — your address stays hidden.</p>
                    </div>
                  )}

                  {/* Title */}
                  <input
                    data-testid="pog-title-input"
                    value={pogForm.title}
                    onChange={(e) => setPogForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Title (e.g. Multi-Family Garage Sale)"
                    className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-fuchsia-400/60 mb-2"
                  />

                  {/* POG Lingo — tap a kool phrase for inspiration */}
                  <div className="mb-3" data-testid="pog-lingo">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-fuchsia-300/90 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> POG Lingo</span>
                      <button
                        type="button"
                        data-testid="pog-lingo-shuffle"
                        onClick={() => setLingoIdx((n) => (n + 4) % POG_LINGO.length)}
                        className="flex items-center gap-1 text-[11px] font-bold text-slate-400 hover:text-fuchsia-300 transition-colors"
                      >
                        <Shuffle className="w-3.5 h-3.5" /> Inspire me
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {[0, 1, 2, 3].map((k) => {
                        const phrase = POG_LINGO[(lingoIdx + k) % POG_LINGO.length];
                        return (
                          <button
                            key={phrase}
                            type="button"
                            data-testid="pog-lingo-chip"
                            onClick={() => setPogForm((f) => ({ ...f, title: (f.title ? `${f.title} ${phrase}` : phrase).slice(0, 120) }))}
                            className="h-7 px-2.5 rounded-full text-[11px] font-bold bg-fuchsia-500/12 text-fuchsia-200 border border-fuchsia-400/25 hover:bg-fuchsia-500/25 transition-colors"
                          >
                            {phrase}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      data-testid="pog-lingo-browse"
                      onClick={() => { setPogOpen(false); navigate("/lingo"); }}
                      className="mt-2 text-[11px] font-bold text-fuchsia-300/80 hover:text-fuchsia-200"
                    >
                      Browse all POG Lingo →
                    </button>
                  </div>

                  {/* When does it end */}
                  <label className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-1.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Ends (auto-removes after)</label>
                  <input
                    data-testid="pog-ends-input"
                    type="datetime-local"
                    value={pogForm.ends}
                    onChange={(e) => setPogForm((f) => ({ ...f, ends: e.target.value }))}
                    className="w-full h-11 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-fuchsia-400/60 mb-3 [color-scheme:dark]"
                  />

                  {/* Description */}
                  <textarea
                    data-testid="pog-detail-input"
                    value={pogForm.detail}
                    onChange={(e) => setPogForm((f) => ({ ...f, detail: e.target.value }))}
                    placeholder="Details (what's there, hours, etc.)"
                    rows={2}
                    className="w-full rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 py-2 text-sm outline-none focus:border-fuchsia-400/60 mb-3 resize-none"
                  />

                  {/* POG Shot — snap a photo of what's for sale (or pick from gallery) */}
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2 mt-1">POG Shot</p>
                  {pogPhotoFile && (
                    <div className="relative mb-2 rounded-xl overflow-hidden">
                      <img data-testid="pog-photo-preview" src={URL.createObjectURL(pogPhotoFile)} alt="POG" className="w-full h-36 object-cover" />
                      <button type="button" onClick={() => setPogPhotoFile(null)} className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-slate-900/70 hover:bg-slate-900 flex items-center justify-center"><X className="w-4 h-4 text-white" /></button>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <label data-testid="pog-shot-camera" className="h-11 rounded-xl bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-400/30 flex items-center justify-center gap-2 cursor-pointer text-sm font-bold text-fuchsia-100">
                      <Camera className="w-4 h-4" /> Take a POG Shot
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPogPhotoFile(e.target.files?.[0] || null)} />
                    </label>
                    <label data-testid="pog-photo-label" className="h-11 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 flex items-center justify-center gap-2 cursor-pointer text-sm font-semibold text-slate-300">
                      <Upload className="w-4 h-4 text-sky-400" /> Upload
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setPogPhotoFile(e.target.files?.[0] || null)} />
                    </label>
                  </div>

                  <p className="text-[11px] text-slate-500 mb-3 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" /> Pinned at your current map center — pan the map first to adjust.</p>

                  {pogForm.err && <p data-testid="pog-error" className="text-rose-400 text-sm mb-2">{pogForm.err}</p>}

                  <button
                    data-testid="pog-submit-btn"
                    onClick={submitPog}
                    disabled={pogBusy}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-heading font-bold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60"
                  >
                    {pogBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PartyPopper className="w-5 h-5" />} Drop my POG
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* POG detail panel (tap a POG tab / open a shared POG link) */}
      <PogDetailPanel
        pogDetail={pogDetail}
        onClose={() => setPogDetail(null)}
        pogId={pogId}
        appInstalled={appInstalled}
        promptInstall={promptInstall}
        driver={driver}
        loginGoogle={loginGoogle}
        pogTrack={pogTrack}
        onCompose={(payload) => { setDmText(""); setDmSent(false); setDmCompose(payload); }}
        routeTo={routeTo}
        sharePog={sharePog}
        copied={copied}
      />


      {/* Location search modal */}
      <LocationSearchModal
        open={locOpen}
        onClose={() => setLocOpen(false)}
        travelMode={travelMode}
        requestGeo={requestGeo}
        locQuery={locQuery}
        setLocQuery={setLocQuery}
        doGeoSearch={doGeoSearch}
        locSearching={locSearching}
        searchAsDeals={searchAsDeals}
        locResults={locResults}
        setExplore={setExplore}
      />

      {/* Ads / Sales panel (double-tap) */}
      <DealsPanel
        spotlight={ads?.business_id ? spotlightIds.get(ads.business_id) : null}
        ads={ads}
        onClose={() => setAds(null)}
        shareDeal={shareDeal}
        copied={copied}
        openLink={openLink}
        adsLoading={adsLoading}
        startRedeem={startRedeem}
        redeemBusy={redeemBusy}
        routeTo={routeTo}
        checkIn={checkIn}
        checkinBusy={checkinBusy}
        checkinMsg={checkinMsg}
        driver={driver}
        userPos={userPos}
        distMiles={distMiles}
        showVisitQr={showVisitQr}
        visitQrBusy={visitQrBusy}
        navigate={navigate}
        inviteBusiness={inviteBusiness}
      />

      {/* In-app delivery request / message compose — keeps the deal on-platform */}
      <AnimatePresence>
        {dmCompose && (
          <motion.div
            data-testid="dm-compose-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2100] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDmCompose(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl bg-gradient-to-b from-slate-900 to-slate-950 border border-amber-400/30 p-6 relative"
            >
              <button data-testid="dm-compose-close" onClick={() => setDmCompose(null)} className="absolute top-3 right-3 w-9 h-9 rounded-xl hover:bg-slate-800 flex items-center justify-center">
                <X className="w-5 h-5 text-slate-300" />
              </button>
              {dmSent ? (
                <div data-testid="dm-compose-sent" className="text-center py-2">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3"><Check className="w-8 h-8 text-emerald-400" /></div>
                  <p className="font-heading text-xl font-extrabold text-emerald-300">Request sent! 🛹</p>
                  <p className="text-slate-300 text-sm mt-2">The seller will reply right here in your AIR.POG inbox.</p>
                  <button data-testid="dm-go-inbox-btn" onClick={() => { setDmCompose(null); navigate("/inbox"); }} className="mt-5 w-full h-12 rounded-2xl bg-sky-500 text-slate-900 font-heading font-bold">Go to my inbox</button>
                </div>
              ) : (
                <>
                  <MessageCircle className="w-9 h-9 text-amber-400 mb-2" />
                  <p className="font-heading text-xl font-bold">Request delivery</p>
                  <p className="text-slate-400 text-sm mt-1 mb-3">Message <span className="text-white font-semibold">{dmCompose.title}</span> — stays in AIR.POG, no address needed.</p>
                  <textarea
                    data-testid="dm-compose-input"
                    value={dmText}
                    onChange={(e) => setDmText(e.target.value.slice(0, 1000))}
                    rows={4}
                    placeholder="Hi! Is this still available? Could you deliver to my ZIP…?"
                    className="w-full rounded-xl bg-slate-800/70 border border-slate-700/60 p-3 text-sm outline-none focus:border-amber-400/60 resize-none"
                  />
                  <button
                    data-testid="dm-compose-send"
                    onClick={sendDm}
                    disabled={dmBusy || !dmText.trim()}
                    className="mt-3 w-full h-12 rounded-2xl bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 font-heading font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {dmBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send request
                  </button>
                  {dmCompose.contact && contactAction(dmCompose.contact)?.href && (
                    <a data-testid="dm-external-contact" href={contactAction(dmCompose.contact).href} target="_blank" rel="noreferrer" className="block text-center text-[11px] text-slate-400 hover:text-slate-200 mt-3">or contact the seller directly</a>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🎉 Arrival celebration — POGZILLA welcomes the POG'R when they roll up (never over the redeem QR) */}
      <ArrivalCelebration
        show={showArrival && !redeem}
        businessName={ads?.business_name}
        onClose={() => setShowArrival(false)}
        onShowQr={() => { setShowArrival(false); showVisitQr(ads.business); }}
        onShare={shareArrivalMoment}
        sharing={sharingMoment}
        shareMsg={shareMsg}
      />

      {/* POG BUCKZ visit QR — driver shows this round "milk-cap" code to the merchant */}
      <AnimatePresence>
        {visitQr && (
          <motion.div
            data-testid="visit-qr-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-slate-950/95 backdrop-blur-sm flex flex-col items-center justify-start overflow-y-auto py-6 px-4"
            onClick={() => setVisitQr(null)}
          >
            <button
              data-testid="visit-qr-close"
              onClick={() => setVisitQr(null)}
              className="absolute top-4 right-4 w-11 h-11 rounded-xl glass hover:bg-slate-700/60 flex items-center justify-center z-10"
            >
              <X className="w-5 h-5 text-slate-200" />
            </button>
            <motion.div
              initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl bg-gradient-to-b from-slate-900 to-slate-950 border border-fuchsia-500/30 shadow-2xl shadow-fuchsia-500/10 p-6 text-center mt-8"
            >
              <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1">
                <Coins className="w-3.5 h-3.5" /> Earn POG BUCKZ
              </span>
              <p className="text-slate-400 text-xs mt-4 font-semibold uppercase tracking-wider">{visitQr.business_name}</p>
              <p className="font-heading text-xl font-bold text-white mt-1">Show this to the shop</p>
              <p className="text-slate-300 text-sm mt-1">They scan your POG cap to bank <span className="font-bold text-amber-300">+{visitQr.reward} POG BUCKZ</span>.</p>

              {/* Round milk-cap frame around the (square) scannable QR */}
              <div className="relative mx-auto mt-6 w-56 h-56">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-400 shadow-[0_0_40px_rgba(251,191,36,0.35)]" />
                <div className="absolute inset-[6px] rounded-full bg-white" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white rounded-2xl p-2">
                    <QRCodeSVG
                      value={visitQr.scan_url}
                      size={132}
                      level="H"
                      imageSettings={{ src: "/pogzilla.png", height: 30, width: 30, excavate: true }}
                    />
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-slate-400 mt-5 flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" /> Code is good for 30 minutes · one scan
              </p>
              {visitQr.code && (
                <div className="mt-4 rounded-2xl bg-slate-800/60 border border-slate-700/60 px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Can't scan? Read this to the shop</p>
                  <p data-testid="visit-qr-code" className="font-mono font-black text-2xl tracking-[0.25em] text-fuchsia-200 mt-1">{visitQr.code}</p>
                  <p className="text-[11px] text-slate-500 mt-1">They enter it at <span className="text-sky-300 font-semibold">airpog.com/scan</span></p>
                </div>
              )}
              <p className="text-[11px] text-slate-500 mt-3">Or tap <span className="text-amber-300 font-semibold">"Check in here"</span> on the shop's panel to earn by GPS. No account? Your POG BUCKZ won't save — log in free to keep every buck.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ask AIR.POG modal */}
      <AnimatePresence>
        {askOpen && (
          <motion.div
            className="absolute inset-0 z-[2100] flex items-start justify-center p-4 pt-20 sm:pt-24 bg-black/50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setAskOpen(false)}
          >
            <motion.div
              data-testid={GPS.askPanel}
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-3xl w-full max-w-md p-6 relative"
            >
              <button data-testid={GPS.askPanelClose} onClick={() => setAskOpen(false)} className="absolute top-4 right-4 w-10 h-10 rounded-xl hover:bg-slate-700/60 flex items-center justify-center">
                <X className="w-5 h-5 text-slate-300" />
              </button>
              <div className="flex items-center gap-3 mb-1">
                <img src={AIRPOG_LOGO} alt="AIR.POG" className="w-9 h-9 rounded-xl object-contain" />
                <div>
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase bg-gradient-to-r from-sky-400 to-violet-400 bg-clip-text text-transparent">Ask AIR.POG</p>
                  <h2 className="font-heading text-xl font-bold leading-tight">What are you looking for?</h2>
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">Try “coffee with a deal nearby” or “a pharmacy that’s open”.</p>

              <div className="flex gap-2 mb-3">
                <input
                  data-testid={GPS.askInput}
                  value={askQuery}
                  onChange={(e) => setAskQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") askAirpog(); }}
                  autoFocus
                  placeholder="Ask anything about nearby places…"
                  className="flex-1 h-12 rounded-xl bg-slate-800/70 px-4 text-sm outline-none focus:ring-2 ring-sky-500/60 placeholder:text-slate-500"
                />
                <button
                  data-testid={GPS.askSubmit}
                  onClick={askAirpog}
                  disabled={askLoading || !askQuery.trim()}
                  className="w-12 h-12 rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 flex items-center justify-center disabled:opacity-50"
                >
                  {askLoading ? <Loader2 className="w-5 h-5 animate-spin text-white" /> : <Send className="w-5 h-5 text-white" />}
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {ASK_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`ask-suggestion-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => askAirpog(s)}
                    disabled={askLoading}
                    className="text-xs font-medium rounded-full px-3 py-1.5 bg-slate-800/70 hover:bg-slate-700/70 text-slate-200 border border-slate-700/60 transition-colors disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>

              {askResult && (
                <div data-testid={GPS.askResult} className="space-y-3">
                  <div className="bg-slate-800/60 rounded-2xl p-4 flex gap-3">
                    <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
                    <p className="text-sm leading-snug text-slate-100">{askResult.answer}</p>
                  </div>
                  <div className="space-y-2 max-h-[38vh] overflow-y-auto no-scrollbar">
                    {askResult.businesses.map((b) => {
                      const Ico = getIcon(b.icon);
                      return (
                        <button
                          key={b.id}
                          onClick={() => routeFromAsk(b)}
                          className="w-full text-left bg-slate-800/50 hover:bg-slate-700/60 rounded-xl p-3 flex items-center gap-3 transition-colors"
                        >
                          <div className="bg-sky-500/15 rounded-lg p-2 shrink-0">
                            {b.logo_url ? (
                              <img src={`${BACKEND_URL}${b.logo_url}`} alt="" className="w-5 h-5 object-contain rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <Ico className="w-5 h-5 text-sky-400" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-heading font-semibold text-sm truncate">{b.name}</p>
                            <p className="text-[11px] text-slate-400 truncate">{t(`categories.${b.category_id}`, { defaultValue: b.category_label })} · {b.distance_miles} mi</p>
                          </div>
                          {b.has_deals && <BadgePercent className="w-4 h-4 text-emerald-400 shrink-0" />}
                          <Navigation className="w-4 h-4 text-sky-400 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AIR.POG Plus modal */}
      <AnimatePresence>
        {plusOpen && (
          <motion.div
            className="absolute inset-0 z-[2100] flex items-start justify-center p-4 pt-20 sm:pt-24 bg-black/50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPlusOpen(false)}
          >
            <motion.div
              data-testid="plus-panel"
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-3xl w-full max-w-md p-6 relative"
            >
              <button onClick={() => setPlusOpen(false)} className="absolute top-4 right-4 w-10 h-10 rounded-xl hover:bg-slate-700/60 flex items-center justify-center">
                <X className="w-5 h-5 text-slate-300" />
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-2.5"><Crown className="w-6 h-6 text-slate-900" /></div>
                <div>
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-amber-300">AIR.POG Plus</p>
                  <h2 className="font-heading text-xl font-bold leading-tight">Your premium driver perks</h2>
                </div>
              </div>

              <p data-testid="plus-free-note" className="text-xs text-slate-300 mb-1 leading-relaxed">The AIR.POG map is <span className="text-emerald-300 font-semibold">free forever</span> — no login needed to drive & find deals. <span className="text-white font-semibold">Log in (free)</span> to save your POGZ, POG BUCKS & favorites and sync them across every device. <span className="text-amber-300 font-semibold">Plus</span> ($2.99/mo) unlocks the perks below — first 30 days free.</p>

              {plusNotice && (
                <div data-testid="plus-notice" className={`rounded-xl px-3 py-2 text-sm flex items-center gap-2 mb-3 border ${plusNotice.type === "success" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : plusNotice.type === "error" ? "bg-rose-500/10 border-rose-500/30 text-rose-200" : "bg-sky-500/10 border-sky-500/30 text-sky-200"}`}>
                  {plusNotice.type === "pending" ? <Loader2 className="w-4 h-4 animate-spin" /> : plusNotice.type === "success" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {plusNotice.text}
                </div>
              )}

              <ul className="space-y-2 mb-5 mt-3">
                {[["Ad-free strip", "No Deal of the Day banner pushed at you"], ["Unlimited favorites", "Free saves 20 merchants — Plus has no limit"], ["Deal alerts", "Get pinged when a deal is within a mile"]].map(([t, d]) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div><p className="text-sm font-semibold">{t}</p><p className="text-xs text-slate-400">{d}</p></div>
                  </li>
                ))}
              </ul>

              {!driver ? (
                <button
                  data-testid="plus-google-login"
                  onClick={loginGoogle}
                  className="w-full h-12 rounded-xl bg-white text-slate-900 font-heading font-bold flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                  Log in free with Google
                </button>
              ) : driver.is_plus ? (
                <div>
                  <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-semibold text-amber-200">Plus active · {driver.plus_days_left} day{driver.plus_days_left === 1 ? "" : "s"} left</span>
                  </div>
                  {driver.favorites?.length > 0 && (
                    <p className="text-xs text-slate-400 mb-3">{driver.favorites.length} saved place{driver.favorites.length === 1 ? "" : "s"} ❤️</p>
                  )}
                  <button data-testid="plus-extend-btn" onClick={handlePlusCheckout} disabled={plusBusy} className="w-full h-11 rounded-xl bg-amber-500/20 text-amber-200 font-heading font-bold flex items-center justify-center gap-2 hover:bg-amber-500/30 disabled:opacity-60 mb-2">
                    {plusBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} Extend Plus — $2.99/mo
                  </button>
                  <button onClick={logout} className="w-full h-10 rounded-xl text-slate-400 hover:text-rose-300 text-sm font-semibold flex items-center justify-center gap-2">
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                </div>
              ) : (
                <div>
                  <button data-testid="plus-subscribe-btn" onClick={handlePlusCheckout} disabled={plusBusy} className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-heading font-bold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
                    {plusBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Crown className="w-5 h-5" />} Get Plus — $2.99/mo
                  </button>
                  <button onClick={logout} className="w-full h-10 mt-2 rounded-xl text-slate-400 hover:text-rose-300 text-sm font-semibold flex items-center justify-center gap-2">
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                </div>
              )}
              {driver && (
                <div data-testid="referral-block" className="mt-4 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-violet-400" />
                    <p className="font-heading font-bold text-sm">Invite friends, earn free months</p>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">When a friend joins with your link, you <span className="text-emerald-300 font-semibold">both get +30 days of Plus</span>.</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 bg-slate-800/70 rounded-xl px-3 h-10 flex items-center text-xs text-slate-300 font-mono truncate">
                      {window.location.host}/?ref={driver.referral_code || "…"}
                    </div>
                    <button data-testid="referral-share-btn" onClick={shareReferral} className="shrink-0 h-10 px-3 rounded-xl bg-violet-500/20 text-violet-200 font-heading font-bold text-xs flex items-center gap-1.5 hover:bg-violet-500/30">
                      {refCopied ? <><Check className="w-4 h-4 text-emerald-400" /> Copied</> : <><Share2 className="w-4 h-4" /> Invite</>}
                    </button>
                  </div>
                  {driver.referral_count > 0 && (
                    <p className="text-xs text-emerald-300 mt-2 font-semibold">🎉 {driver.referral_count} friend{driver.referral_count === 1 ? "" : "s"} joined · {driver.referral_count} free month{driver.referral_count === 1 ? "" : "s"} earned</p>
                  )}
                </div>
              )}
              {driver && (
                <div data-testid="my-pogs-block" className="mt-4 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-heading font-bold text-sm flex items-center gap-2"><PartyPopper className="w-4 h-4 text-fuchsia-400" /> My POGs</p>
                    <button data-testid="my-pogs-post-btn" onClick={() => { setPlusOpen(false); openPostPog(); }} className="h-8 px-3 rounded-full bg-fuchsia-500/20 text-fuchsia-200 font-bold text-xs flex items-center gap-1.5 hover:bg-fuchsia-500/30">
                      <PlusCircle className="w-4 h-4" /> Post
                    </button>
                  </div>
                  {myPogs.length === 0 ? (
                    <p className="text-xs text-slate-400">Drop a garage sale, party, or event on the map for people nearby — or share a private one.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-44 overflow-y-auto no-scrollbar">
                      {myPogs.map((p) => (
                        <div key={p.id} data-testid={`my-pog-${p.id}`} className="flex items-center gap-2 bg-slate-800/50 rounded-xl px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate">{p.title}</p>
                            <p className="text-[11px] text-slate-400 truncate">{p.sale_type} · {p.visibility === "private" ? "Private" : "Public"} · {p.active ? (p.when_label || "Active") : "Expired"}</p>
                            <p className="text-[11px] text-fuchsia-300/90 font-semibold mt-0.5">🔥 {p.views || 0} viewed · 🧭 {p.routes || 0} routing</p>
                          </div>
                          <button data-testid={`my-pog-share-${p.id}`} onClick={() => sharePog(p.id)} className="shrink-0 w-8 h-8 rounded-lg hover:bg-slate-700/60 flex items-center justify-center" title="Share"><Share2 className="w-4 h-4 text-sky-400" /></button>
                          <button data-testid={`my-pog-delete-${p.id}`} onClick={() => deletePog(p.id)} className="shrink-0 w-8 h-8 rounded-lg hover:bg-rose-500/20 flex items-center justify-center" title="Remove"><Trash2 className="w-4 h-4 text-rose-400" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-slate-500 text-center mt-3">
                Driving + finding deals is always free. Plus is optional.
                <br />
                <a href="/terms" className="hover:text-slate-300 underline">Terms</a> · <a href="/privacy" className="hover:text-slate-300 underline">Privacy</a> · © {new Date().getFullYear()} AIR.POG™
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plus deal alert toast */}
      <AnimatePresence>
        {dealAlert && (
          <motion.button
            data-testid="plus-deal-alert"
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            onClick={() => { showAds(dealAlert); setDealAlert(null); }}
            className="absolute top-28 left-3 sm:top-32 z-[1180] flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2 max-w-[85vw] bg-slate-700/95 backdrop-blur-md border border-white/20 text-slate-100 shadow-xl"
          >
            <Crown className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="font-heading font-bold text-sm truncate">Deal nearby · {dealAlert.name} ({dealAlert.distance_miles} mi)</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* 🔴 Sale alert — new reel or live broadcast near you */}
      <AnimatePresence>
        {saleAlert && (
          <motion.button
            data-testid="sale-alert"
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            onClick={() => { const t = saleAlert; setSaleAlert(null); if (t.type === "scout") return; if (t.type === "board") { setBoardOpen(true); setBoardUnread(false); return; } navigate(t.type === "blog" ? (t.slug ? `/blog/${t.slug}` : "/blog") : t.type === "live" && t.id ? `/live/${t.id}` : t.type === "golden" ? "/tickets" : t.type === "podium" ? "/pogathon" : t.type === "crew" ? "/crew" : t.type === "gram" && t.id ? `/gram/${t.id}` : t.type === "pic" || t.type === "champ" ? "/flics" : "/reels"); }}
            className="absolute top-28 left-3 sm:top-32 z-[1181] flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2 max-w-[85vw] bg-slate-700/95 backdrop-blur-md border border-white/20 text-slate-100 shadow-xl"
          >
            <span className="animate-pulse shrink-0">🔴</span>
            <span className="font-heading font-bold text-sm truncate">
              {saleAlert.raw
                ? saleAlert.raw
                : saleAlert.follow
                ? `@${saleAlert.name} you follow ${saleAlert.type === "live" ? "is LIVE" : saleAlert.type === "pic" ? "dropped a deal pic 📸" : "posted a reel"} — ${saleAlert.title}`
                : saleAlert.type === "live" ? (saleAlert.beamed ? `📡 A live just BEAMED into your zone · @${saleAlert.name} — ${saleAlert.title}` : `LIVE near you · @${saleAlert.name} — ${saleAlert.title}`) : `Sale happening now · ${saleAlert.title}`}
              {saleAlert.dist != null ? ` (${saleAlert.dist} mi)` : ""}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* 🧳 TRAVELER MODE — far from home base, scout the new town */}
      {travelerMode && (
        <div className="absolute top-40 left-3 sm:top-44 z-[1179] flex items-center gap-2 rounded-2xl pl-3 pr-2 py-2 max-w-[85vw] bg-slate-700/95 backdrop-blur-md border border-white/20 shadow-xl">
          <button data-testid="traveler-mode-banner" onClick={() => { dismissTraveler(); navigate("/lts"); }} className="flex items-center gap-2 text-left">
            <span className="text-lg">🧳</span>
            <span className="text-[11px] font-black text-slate-100 leading-tight">NEW CITY DETECTED — tap for the LOCAL TRAVEL SCOUT ⚜</span>
          </button>
          <button data-testid="traveler-mode-dismiss" onClick={dismissTraveler} className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <X className="w-3.5 h-3.5 text-slate-300" />
          </button>
        </div>
      )}

      {/* 🚢 POG LINER — cruise ship sails the sky show, tap to honk → /travel */}
      {!calm && (
        <button data-testid="pog-liner" onClick={() => { linerHonk(); toast("🚢 TOOOOT! The POG LINER sails for /travel — all aboard!", { duration: 3000 }); navigate("/travel"); }}
          title="The POG LINER — tap to honk"
          className="absolute top-[26%] left-0 z-[1048] pointer-events-auto liner-sail flex items-center gap-1 rounded-full pl-2 pr-3 py-1 bg-slate-700/90 backdrop-blur-md border border-white/20 shadow-lg">
          <span className="text-xl leading-none">🚢</span>
          <span className="text-[9px] font-black tracking-widest text-sky-300 whitespace-nowrap">POG LINER</span>
        </button>
      )}

      {/* 👑 TOP RUNNER crown — roams the map skies, tap → /runs leaderboard */}
      {runChamp && (
        <button data-testid="top-runner-crown" onClick={() => navigate("/runs")}
          title={`TOP RUNNER of the week: ${runChamp.name}`}
          className="absolute top-[42%] left-0 z-[1049] pointer-events-auto crown-roam flex items-center gap-1.5 rounded-full pl-2 pr-3 py-1.5 bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500 border border-amber-200/80 shadow-lg shadow-amber-500/40">
          <span className="text-base leading-none">👑</span>
          <span className="text-[10px] font-black tracking-wide text-slate-900 whitespace-nowrap">TOP RUNNER · {runChamp.name}</span>
        </button>
      )}

      {/* Choose-your-GPS sheet (Navigate handoff) */}
      <AnimatePresence>
        {gpsSheet && (
          <motion.div
            className="absolute inset-0 z-[2100] flex items-end sm:items-center justify-center p-4 bg-black/50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setGpsSheet(null)}
          >
            <motion.div
              data-testid="gps-chooser-sheet"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-3xl w-full max-w-sm p-5 relative"
            >
              <button onClick={() => setGpsSheet(null)} className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-slate-700/60 flex items-center justify-center">
                <X className="w-4 h-4 text-slate-300" />
              </button>
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-sky-400 mb-1">Navigate with</p>
              <h2 className="font-heading text-lg font-bold leading-tight mb-1 truncate">{gpsSheet.name}</h2>
              <p className="text-xs text-slate-400 mb-4">Open turn-by-turn in your favorite app. We'll remember your pick.</p>
              <div className="space-y-2">
                {Object.entries(GPS_APPS).map(([key, app]) => (
                  <button
                    key={key}
                    data-testid={`gps-app-${key}`}
                    onClick={() => openNav(key, gpsSheet, true)}
                    className="w-full h-12 rounded-xl bg-slate-800/60 hover:bg-slate-700/70 flex items-center gap-3 px-4 transition-colors"
                  >
                    <Navigation className="w-5 h-5 text-sky-400 shrink-0" />
                    <span className="font-heading font-bold text-sm flex-1 text-left">{app.label}</span>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </button>
                ))}
              </div>
              <button
                data-testid="gps-forget-pref"
                onClick={() => { localStorage.removeItem("airpog_gps_pref"); }}
                className="w-full mt-3 text-center text-[11px] text-slate-500 hover:text-slate-300 font-semibold"
              >
                Reset my default GPS app
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite & earn banner (logged-in drivers, dismissible) */}
      <AnimatePresence>
        {driver?.referral_code && !inviteDismissed && !nav && !ads && !featuredBiz && !(geoUncertain && !geoDismissed) && (
          <motion.div
            data-testid="invite-banner"
            initial={{ opacity: 0, y: -16, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -16, x: "-50%" }}
            className={`absolute ${showTrending ? "top-[9.5rem] sm:top-[10rem]" : "top-[6.5rem] sm:top-[7.5rem]"} left-1/2 -translate-x-1/2 z-[1150] flex items-center gap-2 rounded-full pl-3 pr-2 py-1.5 max-w-[92vw] glass-strong border border-violet-400/40 shadow-xl`}
          >
            <button onClick={() => setPlusOpen(true)} className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-4 h-4 text-violet-400 shrink-0" />
              <span className="font-heading font-bold text-sm text-violet-100 truncate">Invite a friend → you both get a free month</span>
            </button>
            <button
              data-testid="invite-banner-dismiss"
              onClick={() => { setInviteDismissed(true); localStorage.setItem("airpog_invite_dismissed", "1"); }}
              className="shrink-0 w-6 h-6 rounded-full hover:bg-slate-700/60 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 text-slate-300" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Exclusive deal redemption screen — show/scan at the store */}
      <AnimatePresence>
        {redeem && (
          <motion.div
            data-testid="redeem-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-slate-950/95 backdrop-blur-sm flex flex-col items-center justify-start overflow-y-auto py-6 px-4"
          >
            <button
              data-testid="redeem-close"
              onClick={() => setRedeem(null)}
              className="absolute top-4 right-4 w-11 h-11 rounded-xl glass hover:bg-slate-700/60 flex items-center justify-center z-10"
            >
              <X className="w-5 h-5 text-slate-200" />
            </button>

            <motion.div
              initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }}
              className="w-full max-w-sm rounded-3xl bg-gradient-to-b from-slate-900 to-slate-950 border border-fuchsia-500/30 shadow-2xl shadow-fuchsia-500/10 p-6 text-center mt-6"
            >
              <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1">
                <Sparkles className="w-3.5 h-3.5" /> Exclusive on AIR.POG
              </span>
              <p className="text-slate-400 text-xs mt-4 font-semibold uppercase tracking-wider">{redeem.business_name}</p>
              <p className="font-heading text-4xl font-extrabold text-emerald-400 mt-1 leading-none">{redeem.discount}</p>
              <p className="font-heading text-lg font-bold text-white mt-2">{redeem.title}</p>
              {redeem.detail && <p className="text-slate-300 text-sm mt-1">{redeem.detail}</p>}

              <div className="bg-white rounded-2xl p-4 mt-5 inline-block">
                <QRCodeSVG value={`${window.location.origin}/verify/${redeem.token}`} size={184} level="M" />
              </div>

              <p className="font-mono font-bold text-2xl tracking-[0.3em] text-white mt-4" data-testid="redeem-code">{redeem.code}</p>

              {redeem.redeemed ? (
                <div className="mt-4">
                  <p data-testid="redeem-status-used" className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-300 bg-amber-400/10 rounded-full px-3 py-1.5">
                    <Check className="w-4 h-4" /> Redeemed
                  </p>
                  <button data-testid="redeem-ascended-link" onClick={() => navigate("/heaven")}
                    className="mt-2.5 block mx-auto text-[12px] font-heading font-black text-sky-300 hover:text-sky-200 transition-colors">
                    ☁️ Your deal has ASCENDED to POG'HEAVEN →
                  </button>
                </div>
              ) : (
                <div className="mt-4 flex items-center justify-center gap-2 text-sky-300">
                  <Radio className="w-4 h-4 animate-pulse" />
                  <span className="font-mono text-sm font-semibold">{new Date(nowTick).toLocaleTimeString()}</span>
                </div>
              )}

              <p className="text-slate-400 text-sm mt-5 leading-snug">
                📲 <span className="text-white font-semibold">Show this screen to staff</span> — they scan the code (or see it live) to apply your deal. Only redeemable through AIR.POG.
              </p>

              <RedeemRateShare redeem={redeem} />
            </motion.div>
            <p className="text-[11px] text-slate-500 mt-4">Live screen · time updates each second · screenshots aren't valid</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* "POG is heating up" milestone toast */}
      <AnimatePresence>
        {milestoneToast && (
          <motion.div
            data-testid="pog-milestone-toast"
            initial={{ opacity: 0, y: 24, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.95 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-[calc(6rem+env(safe-area-inset-bottom))] z-[1700] max-w-[92vw] glass-strong border border-fuchsia-400/40 rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3"
            onClick={() => setMilestoneToast(null)}
          >
            <div className="rounded-xl p-2 bg-fuchsia-500/20 shrink-0"><Flame className="w-5 h-5 text-fuchsia-300" /></div>
            <div className="min-w-0">
              <p className="font-heading font-bold text-sm text-fuchsia-100 leading-tight">🔥 Your POG is heating up!</p>
              <p className="text-xs text-slate-300 truncate">"{milestoneToast.title}" just hit <span className="font-bold text-white">{milestoneToast.milestone}+ views</span> — {milestoneToast.views} total.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Install reward toast */}
      <AnimatePresence>
        {installRewardToast && (
          <motion.div
            data-testid="install-reward-toast"
            initial={{ opacity: 0, y: 24, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.95 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-[calc(6rem+env(safe-area-inset-bottom))] z-[1700] max-w-[92vw] glass-strong border border-emerald-400/40 rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3"
            onClick={() => setInstallRewardToast(null)}
          >
            <div className="rounded-xl p-2 bg-emerald-500/20 shrink-0"><Coins className="w-5 h-5 text-emerald-300" /></div>
            <div className="min-w-0">
              <p className="font-heading font-bold text-sm text-emerald-100 leading-tight">📲 POG'D IT! App installed!</p>
              <p className="text-xs text-slate-300">+{installRewardToast.awarded} POG BUCKS added — balance: <span className="font-bold text-white">{installRewardToast.pog_bucks}</span> 🥞</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* "From the POG BLOG" peek — community discovery on the map */}
      <BlogPeek
        posts={blogPeek}
        visible={blogPeek.length > 0 && !blogPeekDismissed && !nav && !ads && !driving && !exploreActive && !featuredBiz && !redeem && !locOpen && !truckerMode && !milestoneToast && !placingBiz}
        onDismiss={() => { setBlogPeekDismissed(true); sessionStorage.setItem("airpog_blogpeek_dismissed", "1"); }}
      />

      {/* 🎉 You caught an AIR.POG! */}
      <CaughtPogBanner pog={caughtPog} onOpen={() => { setCaughtPog(null); navigate("/collect"); }} />

      {/* 📍🔔 Geo-fence deal alerts — pinged when a live deal is within a quarter mile */}
      <GeoFenceAlerts userPos={userPos} businesses={businesses} onView={showAds} />

      {/* 🏁 POG'ATHON live pill */}
      {/* Pogathon pill removed — map decluttering (Pog'athon lives in the menu) */}

      {/* 🦖 DYNO STAMPEDE — a herd of POGZILLAs charge across (easter egg + siege victory) */}
      {!calm && (
        <>
          <DynoStampede active={pogzilla} />

          {/* 🎈 POGO BALLOON ADS — sponsored balloons drift by all year */}
          <SponsoredBalloons />
          <WanderBalloon />
          <Fireworks />

          {/* 🎺 SKY PARADE — periodic full-fleet flyover */}
          <SkyParade />

          {/* 🌠 SHOOTING STARS — rare night-sky catch */}
          <ShootingStar />
        </>
      )}

      {/* 🧘/🔥/🔔 controls docked into the bottom brand bar (per Sir Eric's mockup) */}

      {/* 🎺 Marshal streak saver — relocated to the MORE menu (map decluttering) */}

      {/* 🎈 BALLOON FEST — balloons drift up the map */}
      <BalloonFestOverlay active={balloons} />

      {/* 🤖⚡ MECHA-POG'ZILLA — rival-lab kaiju sighting (tap to install NEUROLINK CHIPS) */}
      <MechaBoss />

      {/* ⚡🧪 THE CORE — POG'CAPACITOR pill, visible from the kingdom map */}
      <CapacitorPill />

      {/* One-time coach mark — fades in, auto-dismisses after 6s */}
      <CoachMark show={showCoach} onDismiss={dismissCoach} />

      {/* ⚔️ Siege pill retired — lore now lives on the bottom dock's swords button */}

      {/* 🎈 BALLOON FEST countdown teaser (Sep 15 → Oct 1) */}
      <AnimatePresence>
        {balloonCountdown && !teaserGone && (
          <motion.div
            data-testid="balloonfest-countdown"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute top-16 sm:top-20 right-3 z-[1141] flex items-center gap-1.5 rounded-full pl-3 pr-1.5 py-1.5 bg-gradient-to-r from-sky-500 to-fuchsia-500 text-white shadow-lg ring-1 ring-sky-200/50"
          >
            <button onClick={launchBalloons} className="text-[11px] font-heading font-bold whitespace-nowrap">
              🎈 BALLOON FEST in {balloonCountdown} {balloonCountdown === 1 ? "day" : "days"}!
            </button>
            <button data-testid="balloonfest-countdown-dismiss" onClick={dismissTeaser} className="w-5 h-5 rounded-full bg-black/20 hover:bg-black/35 flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🧳 Trip countdown chip — rides above the dock, tap → /travel */}
      {myTrip && (
        <button data-testid="trip-chip" onClick={() => navigate("/travel")}
          className="absolute left-3 bottom-[calc(4.4rem+env(safe-area-inset-bottom))] z-[1003] flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5 bg-slate-700/95 backdrop-blur-md border border-white/20 shadow-lg">
          <span className="text-sm leading-none">🧳</span>
          <span className="text-[10px] font-black text-slate-100 whitespace-nowrap">
            {myTrip.days_left === 0 ? `TRIP DAY — ${myTrip.dest}! 🎉` : `${myTrip.days_left}d to ${myTrip.dest}`}
          </span>
        </button>
      )}

      {/* 🎛️ BOTTOM DOCK — floating glass dock: 🔮 lore · 🎪 sky · 🔔 sound · 🔥 flics (raised FAB) · 🛰 live · ⚜ LTS · 📍 pog'along */}
      <div className="absolute bottom-0 inset-x-0 z-[1002] pb-[calc(0.45rem+env(safe-area-inset-bottom))] px-3 pointer-events-none">
        <div className="pointer-events-auto select-none max-w-md mx-auto h-[3.5rem] rounded-[1.9rem] bg-slate-950/75 backdrop-blur-xl border border-white/15 shadow-[0_10px_36px_rgba(0,0,0,0.6),0_0_28px_rgba(138,0,255,0.3)] px-2.5 flex items-center justify-between">
          <button data-testid="dock-lore-btn" onClick={() => navigate("/legend")} title="THE POG KINGDOM lore 🔮"
            className="w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-lg hover:bg-fuchsia-500/25 active:scale-90 transition-all">🔮</button>
          <button data-testid="calm-mode-toggle" onClick={toggleCalm}
            title={calm ? "Tap for the sky show — trains, balloons & the fleet! 🎪" : "Quiet the sky show — clean map"}
            className={`w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-lg hover:bg-amber-500/25 active:scale-90 transition-all ${calm ? "ring-2 ring-amber-300/70" : ""}`}>{calm ? "🎪" : "🧘"}</button>
          <button data-testid="whistle-toggle" onClick={toggleWhistle}
            title={whistleOn ? "POG EXPRESS whistle is ON — tap to silence" : "Whistle silenced — tap to toot again"}
            className={`w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-lg hover:bg-sky-500/25 active:scale-90 transition-all ${whistleOn ? "" : "opacity-50"}`}>{whistleOn ? "🔔" : "🔕"}</button>
          <button data-testid="flics-map-fab" onClick={() => navigate("/flics")} title="POG PICS & FLICS 🔥 — local deal pics & videos"
            className="-mt-8 w-[3.7rem] h-[3.7rem] rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 ring-4 ring-slate-950/80 border border-amber-200/70 shadow-[0_6px_24px_rgba(251,146,60,0.65)] flex flex-col items-center justify-center active:scale-90 hover:-translate-y-0.5 transition-all">
            <span className="text-2xl leading-none drop-shadow">🔥</span>
            <span className="text-[7px] font-heading font-black tracking-widest text-slate-900 leading-none mt-0.5">FLICS</span>
          </button>
          <button data-testid="dock-live-btn" onClick={() => navigate("/reels")} title="POG LIVE 🛰 — live sales & reels"
            className="w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/10 flex flex-col items-center justify-center hover:bg-rose-500/25 active:scale-90 transition-all">
            <span className="text-base leading-none">🛰️</span>
            <span className="text-[6px] font-heading font-black tracking-widest text-rose-300 leading-none mt-0.5">LIVE</span>
          </button>
          <button data-testid="dock-lts-btn" onClick={() => navigate("/lts")} title="LOCAL TRAVEL SCOUT ⚜ — the best of your town"
            className="w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/10 flex flex-col items-center justify-center hover:bg-amber-500/25 active:scale-90 transition-all">
            <span className="text-base leading-none">⚜️</span>
            <span className="text-[6px] font-heading font-black tracking-widest text-amber-300 leading-none mt-0.5">LTS</span>
          </button>
          <button data-testid="dock-pogalong-btn" onClick={() => navigate("/pogalong")} title="POG'ALONG 📍 — follow the journey"
            className="w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-lg hover:bg-emerald-500/25 active:scale-90 transition-all">📍</button>
        </div>
      </div>

      {!placingBiz && <AskPog
        fabClass="bottom-[calc(8.5rem+env(safe-area-inset-bottom))] right-4"
        panelClass="bottom-[calc(8.5rem+env(safe-area-inset-bottom))] right-4"
        tips={[
          ...(streakRisk ? [`🚨 BEEP-BEEP! Your ${streakRisk}-day deal-posting streak SNAPS at midnight, POG'R! Drop ONE pic or flic to keep the 🔥 alive (streak bonus stacks)!`] : []),
          ...(digest ? [`🕵️📰 DAILY DEAL DIGEST: ${digest.count} fresh REAL deal${digest.count === 1 ? "" : "s"} scouted near you in the last 24h${digest.top?.[0]?.title ? ` — like "${digest.top[0].title}" at ${digest.top[0].name}` : ""}! Tap the 🕵️ spotlights to claim the savings!`] : []),
          ...(balloonCountdown ? [`🎈 BEEP! The ANNUAL BALLOON FEST lifts off in ${balloonCountdown} ${balloonCountdown === 1 ? "day" : "days"}, POG'R! Balloon POGZ incoming!`] : []),
          ...(dailyUnclaimed && new Date().getHours() >= 17 ? ["BEEP-BEEP! ⏰ Your FREE daily POG expires at midnight, POG'R — crack it now and keep that 🔥 streak alive!"] : []),
          ...(dealCount > 0 ? [`BEEP! ${dealCount} live ${dealCount === 1 ? "deal" : "deals"} near you, POG'R! Tap the 🔥 up top to see 'em!`] : []),
          ...(businesses.length > 0 ? [`BEEP-BOOP! ${businesses.length} spots on my radar — check in at one to stack POG BUCKS! 🥞`] : []),
          "Systems POGNIFICENT! Don't forget your FREE daily POG drop — crack it on the collect page! 🃏",
        ]}
      />}
    </div>
  );
}
